import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  ArrowLeft,
  Save,
  Mail,
  Send,
  Plus,
  Trash2,
  Eye,
  Download,
  Check,
  Clock,
  X,
  CheckCircle,
  FileText,
  Info,
  FileQuestion,
  Building,
  Edit2,
  User,
  FileQuestion as FileQuestionAlt,
} from "lucide-react";
import Button from "../ui/Button/Button";
import Input from "../ui/Input/Input";
import Checkbox from "../ui/Checkbox/Checkbox";
import FileUpload from "../FileUpload/FileUpload";
import MultiSelectDropdown from "../MultiSelectDropdown";
import ContactDetailsModal from "../ContactDetailsModal";
import NewContactModal from "../NewContactModal";
import { DiscrepancyService } from "../../services/discrepancyService";
import { SubitemsService } from "../../services/SubitemsService";
import { supabase } from "../../lib/supabase";
import SubitemsModal from "../SubitemsModal";
import IncompleteSubitemsWarningModal from "./IncompleteSubitemsWarningModal";
import DiscrepancyModal from "../Discrepancies/DiscrepancyModal";
import {
  formatEmailToHTML,
  buildStructuredEmail,
} from "../../utils/emailHtmlFormatter";
import { createAndUploadZip } from "../../utils/createZipDownload";
import { ExportAndSendService } from "../../services/exportAndSendService";
import "./ExportForm.css";

const ExportForm = ({
  export: exportData,
  project,
  projectContact,
  clientContacts,
  currentUser,
  onSave,
  onBack,
  onSuccess,
  isCreating,
  onCreateDiscrepancy,
  onDelete,
  refreshTrigger,
}) => {
  const [formData, setFormData] = useState({
    additional_contact_ids: [],
    email_body: "",
    email_html: "",
    email_to: [],
    email_cc: [],
    email_subject: "",
    custom_message: "", // User's custom additions to auto-generated email
    related_discrepancy_ids: [],
    file_formats_needed: [],
    export_date: null, // Will be set by database trigger to Central Time date
    // Design type selections
    design_finish_grade: false,
    design_rough_grade: false,
    design_bottom_of_storm: false,
    design_bottom_of_sanitary: false,
    design_bottom_of_water: false,
    design_other: false,
    design_other_name: "",
    design_erosion_phase_1: false,
    design_erosion_phase_2: false,
    design_erosion_phase_3: false,
    design_erosion_phase_4: false,
    // Legacy files (keep for migration)
    topcon_files: [],
    leica_files: [],
    vcl_files: [],
    dxf_ttm_files: [],
    svd_svl_files: [],
    background_image_files: [],
    control_csv_files: [],
    tbc_files: [],
    // Design-specific file arrays
    finish_grade_topcon_files: [],
    finish_grade_leica_files: [],
    finish_grade_vcl_files: [],
    finish_grade_dxf_ttm_files: [],
    finish_grade_svd_svl_files: [],
    rough_grade_topcon_files: [],
    rough_grade_leica_files: [],
    rough_grade_vcl_files: [],
    rough_grade_dxf_ttm_files: [],
    rough_grade_svd_svl_files: [],
    bottom_storm_topcon_files: [],
    bottom_storm_leica_files: [],
    bottom_storm_vcl_files: [],
    bottom_storm_dxf_ttm_files: [],
    bottom_storm_svd_svl_files: [],
    bottom_sanitary_topcon_files: [],
    bottom_sanitary_leica_files: [],
    bottom_sanitary_vcl_files: [],
    bottom_sanitary_dxf_ttm_files: [],
    bottom_sanitary_svd_svl_files: [],
    bottom_water_topcon_files: [],
    bottom_water_leica_files: [],
    bottom_water_vcl_files: [],
    bottom_water_dxf_ttm_files: [],
    bottom_water_svd_svl_files: [],
    other_design_topcon_files: [],
    other_design_leica_files: [],
    other_design_vcl_files: [],
    other_design_dxf_ttm_files: [],
    other_design_svd_svl_files: [],
    erosion_phase_1_topcon_files: [],
    erosion_phase_1_leica_files: [],
    erosion_phase_1_vcl_files: [],
    erosion_phase_1_dxf_ttm_files: [],
    erosion_phase_1_svd_svl_files: [],
    erosion_phase_2_topcon_files: [],
    erosion_phase_2_leica_files: [],
    erosion_phase_2_vcl_files: [],
    erosion_phase_2_dxf_ttm_files: [],
    erosion_phase_2_svd_svl_files: [],
    erosion_phase_3_topcon_files: [],
    erosion_phase_3_leica_files: [],
    erosion_phase_3_vcl_files: [],
    erosion_phase_3_dxf_ttm_files: [],
    erosion_phase_3_svd_svl_files: [],
    erosion_phase_4_topcon_files: [],
    erosion_phase_4_leica_files: [],
    erosion_phase_4_vcl_files: [],
    erosion_phase_4_dxf_ttm_files: [],
    erosion_phase_4_svd_svl_files: [],
    status: "pending",
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saved', 'saving', 'error', 'unsaved'
  const [lastSaved, setLastSaved] = useState(null);
  const [fileFormatsNeeded, setFileFormatsNeeded] = useState([]);
  const [trimbleFormats, setTrimbleFormats] = useState([]);
  const [availableFileFormats, setAvailableFileFormats] = useState([]);
  const [projectDiscrepancies, setProjectDiscrepancies] = useState([]);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [projectSubitems, setProjectSubitems] = useState([]);
  const [subitemsModalOpen, setSubitemsModalOpen] = useState(false);
  const [loadingSubitems, setLoadingSubitems] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [readyToInvoice, setReadyToInvoice] = useState(null);
  const [hasExplicitlySelectedNo, setHasExplicitlySelectedNo] = useState(false);
  const [completeProject, setCompleteProject] = useState(null);
  const [advancedUtilityExpanded, setAdvancedUtilityExpanded] = useState(false);
  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });
  const [erosionSurfaceExpanded, setErosionSurfaceExpanded] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingAsSent, setMarkingAsSent] = useState(false);
  const [editingDiscrepancy, setEditingDiscrepancy] = useState(null);
  const [showEditDiscrepancyModal, setShowEditDiscrepancyModal] =
    useState(false);
  const [showCreateDiscrepancyModal, setShowCreateDiscrepancyModal] =
    useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [excludedFileTypes, setExcludedFileTypes] = useState({});
  // Fresh project data fetched when modal opens to ensure we have latest ready_to_invoice and modeling_status
  const [freshProjectData, setFreshProjectData] = useState(null);

  // Constants
  const DOWNLOAD_PLACEHOLDER = "[Link will be generated when sending]";

  // Ref for the debounced autosave timer
  const autoSaveTimerRef = useRef(null);

  // Ref for the debounced HTML update timer
  const htmlUpdateTimerRef = useRef(null);

  // Ref to track the current export ID to prevent unnecessary re-initialization
  const currentExportIdRef = useRef(null);

  // Ref for the debounced email generation timer
  const emailGenerationTimerRef = useRef(null);

  // Ref to store latest formData for email generation
  const formDataRef = useRef(formData);

  // Ref for toast notification auto-hide timer
  const toastTimerRef = useRef(null);

  // Ref for custom message textarea to auto-focus on create
  const customMessageRef = useRef(null);

  // Keep ref in sync with formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Toast notification helper
  const showToast = (message, type = "success") => {
    // Clear existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ visible: true, message, type });

    // Auto-hide after 3 seconds
    toastTimerRef.current = setTimeout(() => {
      setToast({ visible: false, message: "", type: "success" });
    }, 3000);
  };

  const trimbleSubFormats = ["VCL", "DXF/TTM", "SVD/SVL", "Background Images"];

  // Helper function to check if client has SVD/SVL support
  const clientHasSvdSvlSupport = () => {
    return (
      trimbleFormats &&
      trimbleFormats.length > 0 &&
      trimbleFormats.some((format) => {
        const lowerFormat = format?.toLowerCase();
        return (
          lowerFormat?.includes("svd") ||
          lowerFormat?.includes("svl") ||
          lowerFormat?.includes("svd/svl")
        );
      })
    );
  };

  // Helper functions for design types
  const getSelectedDesignTypes = () => {
    const types = [];
    if (formData.design_finish_grade)
      types.push({ key: "finish_grade", name: "Finish Grade" });
    if (formData.design_rough_grade)
      types.push({ key: "rough_grade", name: "Rough Grade" });
    if (formData.design_bottom_of_storm)
      types.push({ key: "bottom_storm", name: "Bottom of Storm" });
    if (formData.design_bottom_of_sanitary)
      types.push({ key: "bottom_sanitary", name: "Bottom of Sanitary" });
    if (formData.design_bottom_of_water)
      types.push({ key: "bottom_water", name: "Bottom of Water" });
    if (formData.design_other && formData.design_other_name)
      types.push({ key: "other_design", name: formData.design_other_name });
    if (formData.design_erosion_phase_1)
      types.push({ key: "erosion_phase_1", name: "Erosion Phase 1" });
    if (formData.design_erosion_phase_2)
      types.push({ key: "erosion_phase_2", name: "Erosion Phase 2" });
    if (formData.design_erosion_phase_3)
      types.push({ key: "erosion_phase_3", name: "Erosion Phase 3" });
    if (formData.design_erosion_phase_4)
      types.push({ key: "erosion_phase_4", name: "Erosion Phase 4" });
    return types;
  };

  // Helper functions for excluded file types
  const getFieldKey = (designKey, fileType) => `${designKey}_${fileType}`;

  const toggleExcludeFileType = (designKey, fileType) => {
    const key = getFieldKey(designKey, fileType);
    const wasExcluded = excludedFileTypes[key];

    const newExcludedFileTypes = {
      ...excludedFileTypes,
      [key]: !excludedFileTypes[key],
    };

    console.log("📋 Toggle file type exclusion:", {
      key,
      wasExcluded,
      newValue: newExcludedFileTypes[key],
      allExcluded: newExcludedFileTypes,
    });

    setExcludedFileTypes(newExcludedFileTypes);

    // Trigger debounced email generation when file format toggles change
    debouncedEmailGeneration();

    // Trigger autosave IMMEDIATELY (not debounced) to persist excluded file types
    // This avoids closure issues with debounced functions losing the data
    const updatedData = {
      ...formData,
      excluded_file_types: newExcludedFileTypes,
    };
    console.log(
      "🚀 About to call debouncedAutoSave with excluded_file_types:",
      updatedData.excluded_file_types,
    );
    debouncedAutoSave(updatedData, true); // immediate = true

    // Clear files if excluding - pass newExcludedFileTypes to preserve them
    if (!wasExcluded) {
      handleFileChangeWithExclusions(
        designKey,
        `${fileType}_files`,
        [],
        newExcludedFileTypes,
      );
    }
  };

  const handleFileChangeWithExclusions = (
    designKey,
    fileType,
    files,
    newExcludedTypes,
  ) => {
    const fieldName = `${designKey}_${fileType}`;
    const updated = {
      ...formData,
      [fieldName]: files,
      excluded_file_types: newExcludedTypes || excludedFileTypes, // Use new types if provided
    };
    setFormData(updated);
    debouncedAutoSave(updated, true);
  };

  // Validate required files based on selected design types and formats
  const validateRequiredFiles = () => {
    const selectedDesignTypes = getSelectedDesignTypes();
    const selectedFormats = formData.file_formats_needed || [];
    const missingFields = [];

    // Check if any design types are selected
    if (selectedDesignTypes.length === 0) {
      return { isValid: true, missingFields: [] };
    }

    // Check if any file formats are selected
    if (selectedFormats.length === 0) {
      return { isValid: true, missingFields: [] };
    }

    // For each selected design type, check ALL file types for ALL selected formats
    selectedDesignTypes.forEach((designType) => {
      selectedFormats.forEach((format) => {
        if (format === "Topcon") {
          const fieldKey = `${designType.key}_topcon`;
          if (!excludedFileTypes[fieldKey]) {
            const files = formData[`${designType.key}_topcon_files`] || [];
            if (files.length === 0) {
              missingFields.push({
                id: `file-upload-${fieldKey}`,
                label: `${designType.name} - Topcon files`,
              });
            }
          }
        } else if (format === "Leica") {
          const fieldKey = `${designType.key}_leica`;
          if (!excludedFileTypes[fieldKey]) {
            const files = formData[`${designType.key}_leica_files`] || [];
            if (files.length === 0) {
              missingFields.push({
                id: `file-upload-${fieldKey}`,
                label: `${designType.name} - Leica files`,
              });
            }
          }
        } else if (format === "Trimble") {
          // Check each Trimble subformat based on client settings

          // VCL - always show for Trimble clients
          if (
            !trimbleFormats ||
            trimbleFormats.length === 0 ||
            trimbleFormats.some((f) => f?.toLowerCase().includes("vcl"))
          ) {
            const fieldKey = `${designType.key}_vcl`;
            if (!excludedFileTypes[fieldKey]) {
              const files = formData[`${designType.key}_vcl_files`] || [];
              if (files.length === 0) {
                missingFields.push({
                  id: `file-upload-${fieldKey}`,
                  label: `${designType.name} - Trimble VCL files`,
                });
              }
            }
          }

          // DXF/TTM - always required for Trimble
          const dxfFieldKey = `${designType.key}_dxf_ttm`;
          if (!excludedFileTypes[dxfFieldKey]) {
            const files = formData[`${designType.key}_dxf_ttm_files`] || [];
            if (files.length === 0) {
              missingFields.push({
                id: `file-upload-${dxfFieldKey}`,
                label: `${designType.name} - Trimble DXF/TTM files`,
              });
            }
          }

          // SVD/SVL - always shown, validate if not excluded
          const svdSvlFieldKey = `${designType.key}_svd_svl`;
          if (!excludedFileTypes[svdSvlFieldKey]) {
            const files = formData[`${designType.key}_svd_svl_files`] || [];
            if (files.length === 0) {
              missingFields.push({
                id: `file-upload-${svdSvlFieldKey}`,
                label: `${designType.name} - Trimble SVD/SVL files`,
              });
            }
          }
        }
      });
    });

    // Check background images if Trimble selected and not excluded
    if (
      selectedFormats.includes("Trimble") &&
      !excludedFileTypes["background_image"]
    ) {
      const files = formData.background_image_files || [];
      if (files.length === 0) {
        missingFields.push({
          id: "file-upload-background-images",
          label: "Trimble Background Images",
        });
      }
    }

    // Check control CSV if Trimble selected and not excluded
    if (
      selectedFormats.includes("Trimble") &&
      !excludedFileTypes["control_csv"]
    ) {
      const files = formData.control_csv_files || [];
      if (files.length === 0) {
        missingFields.push({
          id: "file-upload-control-csv",
          label: "Trimble Control File",
        });
      }
    }

    // Check TBC files if Trimble selected and not excluded
    if (selectedFormats.includes("Trimble") && !excludedFileTypes["tbc"]) {
      const files = formData.tbc_files || [];
      if (files.length === 0) {
        missingFields.push({
          id: "file-upload-tbc",
          label: "Trimble TBC Files",
        });
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  };

  // Helper function to check if a field is missing and needs error styling
  const isFieldMissing = (designKey, fileType) => {
    const selectedFormats = formData.file_formats_needed || [];

    // Don't show error if format not selected
    if (fileType === "topcon" && !selectedFormats.includes("Topcon"))
      return false;
    if (fileType === "leica" && !selectedFormats.includes("Leica"))
      return false;
    if (
      (fileType === "vcl" ||
        fileType === "dxf_ttm" ||
        fileType === "svd_svl") &&
      !selectedFormats.includes("Trimble")
    )
      return false;

    // Special handling for Trimble sub-formats based on client settings
    if (
      fileType === "vcl" &&
      trimbleFormats &&
      trimbleFormats.length > 0 &&
      !trimbleFormats.some((f) => f?.toLowerCase().includes("vcl"))
    ) {
      return false; // Client doesn't support VCL
    }
    if (
      fileType === "svd_svl" &&
      trimbleFormats &&
      trimbleFormats.length > 0 &&
      !trimbleFormats.some((f) => {
        const lower = f?.toLowerCase();
        return lower?.includes("svd") || lower?.includes("svl");
      })
    ) {
      return false; // Client doesn't support SVD/SVL
    }

    const fieldKey = `${designKey}_${fileType}`;
    if (excludedFileTypes[fieldKey]) return false; // User excluded it

    const files = formData[`${designKey}_${fileType}_files`] || [];
    return files.length === 0; // Missing if no files uploaded
  };

  // Helper function to get actually included formats based on uploaded files
  // dataSource parameter allows passing fresh data (e.g., from formDataRef.current) to avoid stale state issues
  const getActualIncludedFormats = (dataSource = formData) => {
    const formats = [];
    const selectedDesigns = getSelectedDesignTypes();
    const selectedFormats = dataSource.file_formats_needed || [];

    if (selectedFormats.includes("Topcon")) {
      const hasTopcon = selectedDesigns.some(
        (d) =>
          (dataSource[`${d.key}_topcon_files`] || []).length > 0 &&
          !excludedFileTypes[`${d.key}_topcon`],
      );
      if (hasTopcon) formats.push("Topcon (tp3)");
    }

    if (selectedFormats.includes("Leica")) {
      const hasLeica = selectedDesigns.some(
        (d) =>
          (dataSource[`${d.key}_leica_files`] || []).length > 0 &&
          !excludedFileTypes[`${d.key}_leica`],
      );
      if (hasLeica) formats.push("Leica (landxml, dxf, csv)");
    }

    if (selectedFormats.includes("Trimble")) {
      const trimbleTypes = new Set();
      selectedDesigns.forEach((d) => {
        // VCL files - check if client supports and files exist
        if (
          (!trimbleFormats ||
            trimbleFormats.length === 0 ||
            trimbleFormats.some((f) => f?.toLowerCase().includes("vcl"))) &&
          (dataSource[`${d.key}_vcl_files`] || []).length > 0 &&
          !excludedFileTypes[`${d.key}_vcl`]
        ) {
          trimbleTypes.add("VCL");
        }

        // DXF/TTM files - always available for Trimble
        if (
          (dataSource[`${d.key}_dxf_ttm_files`] || []).length > 0 &&
          !excludedFileTypes[`${d.key}_dxf_ttm`]
        ) {
          trimbleTypes.add("dxf/ttm");
        }

        // SVD/SVL files - check if files exist and not excluded
        if (
          (dataSource[`${d.key}_svd_svl_files`] || []).length > 0 &&
          !excludedFileTypes[`${d.key}_svd_svl`]
        ) {
          trimbleTypes.add("svd/svl");
        }
      });

      if (trimbleTypes.size > 0) {
        const types = Array.from(trimbleTypes).join(", ");
        formats.push(`Trimble (${types})`);
      }
    }

    return formats;
  };

  // Utility function to ensure file structure consistency
  const normalizeFileStructure = (files) => {
    if (!Array.isArray(files)) return [];

    return files
      .map((file) => {
        // If it's already a proper file object, return as-is
        if (file && typeof file === "object" && (file.url || file.publicUrl)) {
          return file;
        }

        // If it's a string (might be just a URL), convert to proper structure
        if (typeof file === "string") {
          return {
            url: file,
            filename: file.split("/").pop() || "Unknown file",
            size: 0,
            type: "unknown",
          };
        }

        // Otherwise, try to extract what we can
        return {
          url: file?.url || file?.publicUrl || file?.public_url || "",
          filename:
            file?.filename ||
            file?.original_filename ||
            file?.name ||
            "Unknown file",
          size: file?.size || 0,
          type: file?.type || "unknown",
        };
      })
      .filter((file) => file.url); // Only keep files with valid URLs
  };

  // Function to update HTML content from plain text
  const updateEmailHTML = useCallback(
    (emailText) => {
      if (!emailText) {
        setFormData((prev) => ({ ...prev, email_html: "" }));
        return;
      }

      // Get user signature
      const userSignature = currentUser?.email_signature_html || null;

      // Convert to HTML (attachment handling done by n8n automation)
      const htmlContent = formatEmailToHTML(emailText, userSignature);

      setFormData((prev) => ({ ...prev, email_html: htmlContent }));
    },
    [
      projectDiscrepancies,
      formData.related_discrepancy_ids,
      currentUser?.email_signature_html,
    ],
  );

  // Debounced HTML update function
  const debouncedHTMLUpdate = useCallback(
    (emailText) => {
      // Clear existing timer
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }

      // Set new timer for 1 second delay
      htmlUpdateTimerRef.current = setTimeout(() => {
        updateEmailHTML(emailText);
      }, 1000);
    },
    [updateEmailHTML],
  );

  useEffect(() => {
    console.log("🔄 ExportForm useEffect triggered", {
      exportData: exportData?.id,
      isCreating,
      formInitialized,
      currentExportIdRef: currentExportIdRef.current,
    });

    if (exportData && !isCreating) {
      const exportId = exportData.id;
      const isExportSwitch = currentExportIdRef.current !== exportId;

      console.log("Export ID check:", {
        current: currentExportIdRef.current,
        new: exportId,
        isSwitch: isExportSwitch,
        formInitialized,
      });

      // If this is just an update to the same export, don't re-initialize
      // to avoid overwriting user changes that are being saved
      if (currentExportIdRef.current === exportId && formInitialized) {
        console.log(
          "✅ Same export updated - skipping re-initialization to preserve user changes",
        );
        return; // Exit early to prevent full re-initialization
      }

      // Store the current export ID
      currentExportIdRef.current = exportId;

      console.log("Loading exportData:", exportData);
      console.log(
        "📅 [FormInit] FULL exportData keys:",
        Object.keys(exportData),
      );
      console.log(
        "📅 [FormInit] export_date from exportData:",
        exportData.export_date,
        "type:",
        typeof exportData.export_date,
      );
      console.log(
        "📅 [FormInit] Does exportData have export_date key?",
        "export_date" in exportData,
      );
      console.log(
        "Export related_discrepancy_ids:",
        exportData.related_discrepancy_ids,
      );
      console.log("🔍 DESIGN TYPES FROM DB:", {
        finish_grade: exportData.design_finish_grade,
        rough_grade: exportData.design_rough_grade,
        other: exportData.design_other,
        bottom_storm: exportData.design_bottom_of_storm,
      });

      // Debug file arrays
      const fileFields = [
        "topcon_files",
        "leica_files",
        "vcl_files",
        "dxf_ttm_files",
        "svd_svl_files",
        "background_image_files",
      ];
      fileFields.forEach((field) => {
        if (exportData[field] && exportData[field].length > 0) {
          console.log(`${field} structure:`, exportData[field]);
          console.log(`First ${field} item:`, exportData[field][0]);
        }
      });

      console.log(
        "🔍 FORM LOADING - exportData.email_to from database:",
        exportData.email_to,
      );

      setFormData({
        additional_contact_ids: exportData.additional_contact_ids || [],
        email_body: exportData.email_body || "",
        email_html: exportData.email_html || "",
        email_to: exportData.email_to || [],
        email_cc: exportData.email_cc || [],
        email_subject: exportData.email_subject || "",
        custom_message: exportData.custom_message || "", // Load custom message from DB
        // Convert string IDs to numbers for consistent type handling
        related_discrepancy_ids: (exportData.related_discrepancy_ids || []).map(
          (id) => (typeof id === "string" ? parseInt(id, 10) : id),
        ),
        file_formats_needed: exportData.file_formats_needed || [],
        // Load export_date from database - extract YYYY-MM-DD part to avoid timezone issues
        export_date: (() => {
          if (!exportData.export_date) {
            // Return null - database trigger will set default on save
            console.log(
              "📅 [FormInit] No export_date in DB, keeping null (will use database default)",
            );
            return null;
          }

          // Debug: log what we're getting from database
          console.log(
            "📅 [FormInit] Raw export_date from DB:",
            exportData.export_date,
            "type:",
            typeof exportData.export_date,
          );

          // If it's already a date string (YYYY-MM-DD), use it
          if (
            typeof exportData.export_date === "string" &&
            exportData.export_date.match(/^\d{4}-\d{2}-\d{2}$/)
          ) {
            console.log(
              "📅 [FormInit] Using date string as-is:",
              exportData.export_date,
            );
            return exportData.export_date;
          }

          // If it's an ISO timestamp string, extract first 10 chars (YYYY-MM-DD)
          if (typeof exportData.export_date === "string") {
            const extracted = exportData.export_date.substring(0, 10);
            console.log(
              "📅 [FormInit] Extracted date from timestamp:",
              extracted,
            );
            return extracted;
          }

          // If it's a Date object, convert to ISO and take first 10 chars
          const converted = new Date(exportData.export_date)
            .toISOString()
            .substring(0, 10);
          console.log(
            "📅 [FormInit] Converted Date object to string:",
            converted,
          );
          return converted;
        })(),
        // Design type selections
        design_finish_grade: exportData.design_finish_grade || false,
        design_rough_grade: exportData.design_rough_grade || false,
        design_bottom_of_storm: exportData.design_bottom_of_storm || false,
        design_bottom_of_sanitary:
          exportData.design_bottom_of_sanitary || false,
        design_bottom_of_water: exportData.design_bottom_of_water || false,
        design_other: exportData.design_other || false,
        design_other_name: exportData.design_other_name || "",
        design_erosion_phase_1: exportData.design_erosion_phase_1 || false,
        design_erosion_phase_2: exportData.design_erosion_phase_2 || false,
        design_erosion_phase_3: exportData.design_erosion_phase_3 || false,
        design_erosion_phase_4: exportData.design_erosion_phase_4 || false,
        // Legacy files (for migration)
        topcon_files: normalizeFileStructure(exportData.topcon_files),
        leica_files: normalizeFileStructure(exportData.leica_files),
        vcl_files: normalizeFileStructure(exportData.vcl_files),
        dxf_ttm_files: normalizeFileStructure(exportData.dxf_ttm_files),
        svd_svl_files: normalizeFileStructure(exportData.svd_svl_files),
        background_image_files: normalizeFileStructure(
          exportData.background_image_files,
        ),
        // Design-specific file arrays
        finish_grade_topcon_files: normalizeFileStructure(
          exportData.finish_grade_topcon_files,
        ),
        finish_grade_leica_files: normalizeFileStructure(
          exportData.finish_grade_leica_files,
        ),
        finish_grade_vcl_files: normalizeFileStructure(
          exportData.finish_grade_vcl_files,
        ),
        finish_grade_dxf_ttm_files: normalizeFileStructure(
          exportData.finish_grade_dxf_ttm_files,
        ),
        finish_grade_svd_svl_files: normalizeFileStructure(
          exportData.finish_grade_svd_svl_files,
        ),
        rough_grade_topcon_files: normalizeFileStructure(
          exportData.rough_grade_topcon_files,
        ),
        rough_grade_leica_files: normalizeFileStructure(
          exportData.rough_grade_leica_files,
        ),
        rough_grade_vcl_files: normalizeFileStructure(
          exportData.rough_grade_vcl_files,
        ),
        rough_grade_dxf_ttm_files: normalizeFileStructure(
          exportData.rough_grade_dxf_ttm_files,
        ),
        rough_grade_svd_svl_files: normalizeFileStructure(
          exportData.rough_grade_svd_svl_files,
        ),
        bottom_storm_topcon_files: normalizeFileStructure(
          exportData.bottom_storm_topcon_files,
        ),
        bottom_storm_leica_files: normalizeFileStructure(
          exportData.bottom_storm_leica_files,
        ),
        bottom_storm_vcl_files: normalizeFileStructure(
          exportData.bottom_storm_vcl_files,
        ),
        bottom_storm_dxf_ttm_files: normalizeFileStructure(
          exportData.bottom_storm_dxf_ttm_files,
        ),
        bottom_storm_svd_svl_files: normalizeFileStructure(
          exportData.bottom_storm_svd_svl_files,
        ),
        bottom_sanitary_topcon_files: normalizeFileStructure(
          exportData.bottom_sanitary_topcon_files,
        ),
        bottom_sanitary_leica_files: normalizeFileStructure(
          exportData.bottom_sanitary_leica_files,
        ),
        bottom_sanitary_vcl_files: normalizeFileStructure(
          exportData.bottom_sanitary_vcl_files,
        ),
        bottom_sanitary_dxf_ttm_files: normalizeFileStructure(
          exportData.bottom_sanitary_dxf_ttm_files,
        ),
        bottom_sanitary_svd_svl_files: normalizeFileStructure(
          exportData.bottom_sanitary_svd_svl_files,
        ),
        bottom_water_topcon_files: normalizeFileStructure(
          exportData.bottom_water_topcon_files,
        ),
        bottom_water_leica_files: normalizeFileStructure(
          exportData.bottom_water_leica_files,
        ),
        bottom_water_vcl_files: normalizeFileStructure(
          exportData.bottom_water_vcl_files,
        ),
        bottom_water_dxf_ttm_files: normalizeFileStructure(
          exportData.bottom_water_dxf_ttm_files,
        ),
        bottom_water_svd_svl_files: normalizeFileStructure(
          exportData.bottom_water_svd_svl_files,
        ),
        other_design_topcon_files: normalizeFileStructure(
          exportData.other_design_topcon_files,
        ),
        other_design_leica_files: normalizeFileStructure(
          exportData.other_design_leica_files,
        ),
        other_design_vcl_files: normalizeFileStructure(
          exportData.other_design_vcl_files,
        ),
        other_design_dxf_ttm_files: normalizeFileStructure(
          exportData.other_design_dxf_ttm_files,
        ),
        other_design_svd_svl_files: normalizeFileStructure(
          exportData.other_design_svd_svl_files,
        ),
        erosion_phase_1_topcon_files: normalizeFileStructure(
          exportData.erosion_phase_1_topcon_files,
        ),
        erosion_phase_1_leica_files: normalizeFileStructure(
          exportData.erosion_phase_1_leica_files,
        ),
        erosion_phase_1_vcl_files: normalizeFileStructure(
          exportData.erosion_phase_1_vcl_files,
        ),
        erosion_phase_1_dxf_ttm_files: normalizeFileStructure(
          exportData.erosion_phase_1_dxf_ttm_files,
        ),
        erosion_phase_1_svd_svl_files: normalizeFileStructure(
          exportData.erosion_phase_1_svd_svl_files,
        ),
        erosion_phase_2_topcon_files: normalizeFileStructure(
          exportData.erosion_phase_2_topcon_files,
        ),
        erosion_phase_2_leica_files: normalizeFileStructure(
          exportData.erosion_phase_2_leica_files,
        ),
        erosion_phase_2_vcl_files: normalizeFileStructure(
          exportData.erosion_phase_2_vcl_files,
        ),
        erosion_phase_2_dxf_ttm_files: normalizeFileStructure(
          exportData.erosion_phase_2_dxf_ttm_files,
        ),
        erosion_phase_2_svd_svl_files: normalizeFileStructure(
          exportData.erosion_phase_2_svd_svl_files,
        ),
        erosion_phase_3_topcon_files: normalizeFileStructure(
          exportData.erosion_phase_3_topcon_files,
        ),
        erosion_phase_3_leica_files: normalizeFileStructure(
          exportData.erosion_phase_3_leica_files,
        ),
        erosion_phase_3_vcl_files: normalizeFileStructure(
          exportData.erosion_phase_3_vcl_files,
        ),
        erosion_phase_3_dxf_ttm_files: normalizeFileStructure(
          exportData.erosion_phase_3_dxf_ttm_files,
        ),
        erosion_phase_3_svd_svl_files: normalizeFileStructure(
          exportData.erosion_phase_3_svd_svl_files,
        ),
        erosion_phase_4_topcon_files: normalizeFileStructure(
          exportData.erosion_phase_4_topcon_files,
        ),
        erosion_phase_4_leica_files: normalizeFileStructure(
          exportData.erosion_phase_4_leica_files,
        ),
        erosion_phase_4_vcl_files: normalizeFileStructure(
          exportData.erosion_phase_4_vcl_files,
        ),
        erosion_phase_4_dxf_ttm_files: normalizeFileStructure(
          exportData.erosion_phase_4_dxf_ttm_files,
        ),
        erosion_phase_4_svd_svl_files: normalizeFileStructure(
          exportData.erosion_phase_4_svd_svl_files,
        ),
        control_csv_files: normalizeFileStructure(exportData.control_csv_files),
        tbc_files: normalizeFileStructure(exportData.tbc_files),
        status: exportData.status || "pending",
      });

      // Load excluded file types from database
      if (exportData.excluded_file_types) {
        console.log(
          "📋 Loading excluded file types from database:",
          exportData.excluded_file_types,
        );
        setExcludedFileTypes(exportData.excluded_file_types);
      } else {
        console.log(
          "📋 No excluded_file_types in database, using empty object",
        );
        setExcludedFileTypes({});
      }

      // Set Advanced Utility expanded if any utility designs are selected
      const hasUtility =
        exportData.design_bottom_of_storm ||
        exportData.design_bottom_of_sanitary ||
        exportData.design_bottom_of_water;
      setAdvancedUtilityExpanded(hasUtility);

      // Set Erosion Surface expanded if any erosion phases are selected
      const hasErosion =
        exportData.design_erosion_phase_1 ||
        exportData.design_erosion_phase_2 ||
        exportData.design_erosion_phase_3 ||
        exportData.design_erosion_phase_4;
      setErosionSurfaceExpanded(hasErosion);

      setFormInitialized(true);
    } else if (isCreating && !formInitialized) {
      // Pre-populate with project's file formats when creating new export
      // Fallback to client brand if project has no file formats
      const projectFormats =
        project.file_formats_needed || project.fileFormatsNeeded;
      let initialFormats = [];

      if (
        projectFormats &&
        Array.isArray(projectFormats) &&
        projectFormats.length > 0
      ) {
        // Use project's file formats
        initialFormats = [...projectFormats];
      } else if (project.clients?.brand) {
        // Fallback to client brand (clean up {Trimble} format)
        const clientBrand = project.clients.brand;
        if (Array.isArray(clientBrand) && clientBrand.length > 0) {
          initialFormats = clientBrand
            .map((b) => {
              if (typeof b === "string") {
                return b.replace(/[{}]/g, "").trim();
              }
              return b;
            })
            .filter((b) => b && b !== "Unknown");
        }
      }

      setFormData((prev) => ({
        ...prev,
        file_formats_needed: initialFormats,
      }));
      setFormInitialized(true);
    }
  }, [
    exportData,
    isCreating,
    project.file_formats_needed,
    project.fileFormatsNeeded,
    project.clients?.brand,
  ]);

  useEffect(() => {
    loadProjectFormats();
    loadProjectDiscrepancies();
    loadProjectSubitems();
  }, [project]);

  // Fetch fresh project data to ensure we have latest ready_to_invoice and modeling_status
  useEffect(() => {
    const fetchFreshProjectData = async () => {
      if (project?.id) {
        try {
          const { data, error } = await supabase
            .from("projects")
            .select("ready_to_invoice, modeling_status")
            .eq("id", project.id)
            .single();

          if (!error && data) {
            setFreshProjectData(data);
          }
        } catch (err) {
          console.error("Error fetching fresh project data:", err);
        }
      }
    };
    fetchFreshProjectData();
  }, [project?.id]);

  // Initialize project action fields from fresh project data (or fallback to prop)
  useEffect(() => {
    const projectData = freshProjectData || project;
    if (projectData) {
      // Initialize ready_to_invoice from project (will be null if not set)
      const invoiceValue =
        projectData.ready_to_invoice || projectData.readyToInvoice || null;
      setReadyToInvoice(invoiceValue);
      // If value is explicitly null and project exists, user hasn't made a choice yet
      setHasExplicitlySelectedNo(false);
      // Initialize complete project from modeling_status
      const isCompleted =
        projectData.modeling_status === "Completed" ||
        projectData.modelingStatus === "Completed";
      setCompleteProject(isCompleted ? true : null);
    }
  }, [project?.id, freshProjectData]);

  // Initialize SVD/SVL exclusion based on client support
  useEffect(() => {
    const hasSvdSupport = clientHasSvdSvlSupport();
    console.log("🔍 useEffect SVD/SVL initialization:", {
      formInitialized,
      hasSvdSupport,
      trimbleFormats,
      willSetToExcluded: formInitialized && !hasSvdSupport,
    });

    if (formInitialized && !hasSvdSupport) {
      console.log(
        "⚠️ Client does NOT support SVD/SVL - setting all to excluded",
      );
      // Set all design types' svd_svl to excluded by default when client doesn't support it
      setExcludedFileTypes((prev) => {
        const updated = { ...prev };
        const designTypes = [
          "finish_grade",
          "rough_grade",
          "bottom_storm",
          "bottom_sanitary",
          "bottom_water",
          "other_design",
          "erosion_phase_1",
          "erosion_phase_2",
          "erosion_phase_3",
          "erosion_phase_4",
        ];
        designTypes.forEach((designType) => {
          const key = `${designType}_svd_svl`;
          // Only set to excluded if not already set (don't override user's choice)
          if (!(key in updated)) {
            updated[key] = true;
          }
        });
        return updated;
      });
    }
  }, [formInitialized, project?.clientId]);

  // Default Control CSV and TBC files to excluded (off by default)
  useEffect(() => {
    if (formInitialized) {
      setExcludedFileTypes((prev) => {
        const updated = { ...prev };
        // Only set to excluded if not already set (don't override saved preference)
        if (!("control_csv" in updated)) {
          updated["control_csv"] = true;
        }
        if (!("tbc" in updated)) {
          updated["tbc"] = true;
        }
        return updated;
      });
    }
  }, [formInitialized]);

  // Reload discrepancies when refreshTrigger changes (e.g., after creating a new discrepancy)
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      loadProjectDiscrepancies();
    }
  }, [refreshTrigger]);

  // Regenerate HTML when custom_message or discrepancies change (if email was already generated)
  useEffect(() => {
    // Only regenerate if email has been generated at least once AND form is initialized
    // Skip during initial load to prevent overwriting excludedFileTypes with stale state
    if (
      formData.email_html &&
      formData.email_html.length > 0 &&
      formInitialized
    ) {
      // Debounce the regeneration to avoid firing on every keystroke
      const timer = setTimeout(() => {
        console.log(
          "🔄 Auto-regenerating email HTML due to custom_message/discrepancy change",
        );
        handleGenerateEmail();
      }, 1500); // 1.5 second debounce

      return () => clearTimeout(timer);
    }
  }, [
    formData.custom_message,
    formData.related_discrepancy_ids,
    formInitialized,
  ]);

  // Separate useEffect for email recipients to ensure proper initialization
  useEffect(() => {
    if (
      project?.contactId &&
      projectContact &&
      (isCreating || !formData.email_to || formData.email_to.length === 0)
    ) {
      console.log(
        "✉️ Setting default email recipient to project contact:",
        project.contactId,
        "projectContact:",
        projectContact,
      );
      setFormData((prev) => ({
        ...prev,
        email_to: [project.contactId],
        email_subject:
          prev.email_subject ||
          `${project.projectName || project.name} - Export Files`,
      }));
    } else {
      console.log("✉️ NOT setting default recipient. Conditions:", {
        hasContactId: !!project?.contactId,
        hasProjectContact: !!projectContact,
        isCreating,
        emailToLength: formData.email_to?.length,
      });
    }
  }, [project, projectContact, isCreating, formData.email_to]);

  // Set initial save status
  useEffect(() => {
    if (isCreating) {
      setSaveStatus("unsaved"); // New exports start as unsaved
    } else if (exportData?.id) {
      setSaveStatus("saved"); // Existing exports start as saved
      setLastSaved(
        exportData.updated_at ? new Date(exportData.updated_at) : new Date(),
      );
    }
  }, [isCreating, exportData?.id, exportData?.updated_at]);

  // Auto-focus custom message field when creating new export
  useEffect(() => {
    if (isCreating && customMessageRef.current) {
      // Delay to ensure DOM is fully rendered and visible
      setTimeout(() => {
        if (
          customMessageRef.current &&
          document.contains(customMessageRef.current)
        ) {
          customMessageRef.current.focus();
        }
      }, 200);
    }
  }, [isCreating]);

  // Cleanup timers on unmount and reset export ID ref
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }
      // Reset the export ID ref when component unmounts
      currentExportIdRef.current = null;
    };
  }, []);

  // Load project discrepancies
  const loadProjectDiscrepancies = async () => {
    if (!project?.id) {
      console.log("🔍 loadProjectDiscrepancies: No project ID, skipping");
      return;
    }

    console.log(
      "🔍 loadProjectDiscrepancies: Loading for project ID:",
      project.id,
    );
    setLoadingDiscrepancies(true);
    try {
      const discrepancies =
        await DiscrepancyService.getDiscrepanciesByProjectId(project.id);
      console.log(
        "🔍 loadProjectDiscrepancies: Fetched discrepancies:",
        discrepancies,
      );
      console.log(
        "🔍 loadProjectDiscrepancies: Count:",
        discrepancies?.length || 0,
      );
      setProjectDiscrepancies(discrepancies || []);
    } catch (err) {
      console.error("❌ Error loading project discrepancies:", err);
      setProjectDiscrepancies([]);
    } finally {
      setLoadingDiscrepancies(false);
    }
  };

  // Load project subitems
  const loadProjectSubitems = async () => {
    if (!project?.id) return;

    setLoadingSubitems(true);
    try {
      const subitems = await SubitemsService.getSubitemsByProject(project.id);
      setProjectSubitems(subitems || []);
    } catch (err) {
      console.error("Error loading project subitems:", err);
      setProjectSubitems([]);
    } finally {
      setLoadingSubitems(false);
    }
  };

  // Calculate incomplete subitems
  const getIncompleteSubitemsCount = () => {
    return projectSubitems.filter((subitem) => {
      const status = subitem.subitemStatus;
      // Consider incomplete if: no status, null, 'Working on it', or 'Stuck'
      return (
        !status ||
        status === null ||
        status === "Working on it" ||
        status === "Stuck"
      );
    }).length;
  };

  const getSubitemsCompletionPercentage = () => {
    if (projectSubitems.length === 0) return 100;
    const completed = projectSubitems.filter((subitem) => {
      const status = subitem.subitemStatus;
      // Only count 'Done' and 'N/A' as completed
      return status === "Done" || status === "N/A";
    }).length;
    return Math.round((completed / projectSubitems.length) * 100);
  };

  const loadProjectFormats = async () => {
    try {
      console.log("Loading project formats for project:", project);

      // Available formats for dropdown - ALWAYS the standard 3 options
      setAvailableFileFormats(["Topcon", "Leica", "Trimble"]);

      // Get project's file formats needed for reference/display only
      const projectFormats =
        project.file_formats_needed || project.fileFormatsNeeded;
      if (projectFormats && Array.isArray(projectFormats)) {
        console.log("Project file formats needed:", projectFormats);
        setFileFormatsNeeded(projectFormats);
      }

      // Check if client already has trimble_formats
      if (project.clients?.trimble_formats) {
        console.log(
          "Client trimble formats from project:",
          project.clients.trimble_formats,
        );
        setTrimbleFormats(project.clients.trimble_formats);
      } else if (
        projectFormats?.includes("Trimble") &&
        (project.clientId || project.client_id)
      ) {
        // Fallback: fetch from database if not in project data
        const { data: clientData } = await supabase
          .from("clients")
          .select("trimble_formats")
          .eq("id", project.clientId || project.client_id)
          .single();

        if (clientData?.trimble_formats) {
          console.log(
            "Client trimble formats from DB:",
            clientData.trimble_formats,
          );
          setTrimbleFormats(clientData.trimble_formats);
        }
      }
    } catch (error) {
      console.error("Error loading project formats:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCreating) {
        await onSave(formData);
      } else {
        await onSave(exportData.id, formData);
      }
    } catch (error) {
      console.error("Error saving export:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isCreating) {
      // If creating new export, just go back
      onBack();
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this export? This action cannot be undone.",
    );

    if (!confirmDelete) {
      return;
    }

    setSaving(true);
    try {
      if (onDelete) {
        await onDelete(exportData.id);
      }
      onBack(); // Go back after successful deletion
    } catch (error) {
      console.error("Error deleting export:", error);
      alert("Failed to delete export. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Enhanced autosave function that handles status updates
  const performAutoSave = useCallback(
    async (updatedFormData) => {
      try {
        setSaveStatus("saving");

        console.log(
          "📥 performAutoSave received updatedFormData.excluded_file_types:",
          updatedFormData.excluded_file_types,
        );
        console.log(
          "📥 performAutoSave current state excludedFileTypes:",
          excludedFileTypes,
        );

        // Convert export_date to ISO timestamp for proper database storage
        // Use excluded_file_types from updatedFormData if present (from toggle),
        // otherwise use from state (for other updates)
        const fileTypesToSave =
          updatedFormData.excluded_file_types || excludedFileTypes;
        const dataToSave = {
          ...updatedFormData,
          excluded_file_types: fileTypesToSave,
        };

        console.log("💾 Autosaving with excluded_file_types:", fileTypesToSave);

        if (
          dataToSave.export_date &&
          typeof dataToSave.export_date === "string"
        ) {
          // If it's a date string (YYYY-MM-DD), convert to ISO timestamp at UTC midnight
          if (dataToSave.export_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dataToSave.export_date = `${dataToSave.export_date}T00:00:00.000Z`;
          }
        }

        if (isCreating || !exportData?.id) {
          // For new exports, create a draft first
          const newExport = await onSave({
            ...dataToSave,
            project_id: project.id,
            status: "draft",
          });

          if (newExport) {
            setLastSaved(new Date());
            setSaveStatus("saved");
            console.log("✅ Saved new export with excluded_file_types");
          }
        } else {
          // Update existing export
          console.log("📝 Updating export ID:", exportData.id, "with data:", {
            excluded_file_types: dataToSave.excluded_file_types,
          });
          await onSave(exportData.id, dataToSave);
          setLastSaved(new Date());
          setSaveStatus("saved");
          console.log("✅ Saved existing export with excluded_file_types");
        }
      } catch (error) {
        console.error("Error autosaving export:", error);
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("unsaved"), 3000);
      }
    },
    [isCreating, exportData?.id, onSave, project.id, excludedFileTypes],
  );

  // Debounced autosave function
  const debouncedAutoSave = useCallback(
    (updatedFormData, immediate = false) => {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      if (!immediate) {
        setSaveStatus("unsaved");
      }

      if (immediate) {
        performAutoSave(updatedFormData);
      } else {
        autoSaveTimerRef.current = setTimeout(() => {
          performAutoSave(updatedFormData);
        }, 2000);
      }
    },
    [performAutoSave],
  );

  // Legacy autoSave function for backward compatibility
  const autoSave = (updatedFormData) => {
    debouncedAutoSave(updatedFormData, true);
  };

  const handleDesignTypeChange = (designKey, checked) => {
    console.log("🔄 Design type change:", {
      designKey,
      checked,
      currentValue: formData[designKey],
    });
    const updated = { ...formData, [designKey]: checked };

    // Auto-expand Advanced Utility Mode when selecting utility options
    if (
      [
        "design_bottom_of_storm",
        "design_bottom_of_sanitary",
        "design_bottom_of_water",
      ].includes(designKey) &&
      checked
    ) {
      setAdvancedUtilityExpanded(true);
    }

    // Auto-collapse if no utility options are selected
    if (
      [
        "design_bottom_of_storm",
        "design_bottom_of_sanitary",
        "design_bottom_of_water",
      ].includes(designKey) &&
      !checked
    ) {
      const hasUtilitySelected =
        updated.design_bottom_of_storm ||
        updated.design_bottom_of_sanitary ||
        updated.design_bottom_of_water;
      if (!hasUtilitySelected) {
        setAdvancedUtilityExpanded(false);
      }
    }

    // Auto-expand Erosion Surface when selecting erosion options
    if (
      [
        "design_erosion_phase_1",
        "design_erosion_phase_2",
        "design_erosion_phase_3",
        "design_erosion_phase_4",
      ].includes(designKey) &&
      checked
    ) {
      setErosionSurfaceExpanded(true);
    }

    // Auto-collapse if no erosion options are selected
    if (
      [
        "design_erosion_phase_1",
        "design_erosion_phase_2",
        "design_erosion_phase_3",
        "design_erosion_phase_4",
      ].includes(designKey) &&
      !checked
    ) {
      const hasErosionSelected =
        updated.design_erosion_phase_1 ||
        updated.design_erosion_phase_2 ||
        updated.design_erosion_phase_3 ||
        updated.design_erosion_phase_4;
      if (!hasErosionSelected) {
        setErosionSurfaceExpanded(false);
      }
    }

    // Auto-enable svd/svl field if client supports it and design is being selected
    const hasSvdSupport = clientHasSvdSvlSupport();
    console.log("🔍 SVD/SVL Auto-enable check:", {
      designKey,
      checked,
      hasSvdSupport,
      trimbleFormats,
      excludedFileTypes,
    });

    if (checked && hasSvdSupport) {
      // Convert design key to file field key
      // design_finish_grade -> finish_grade
      // design_bottom_of_storm -> bottom_storm
      // design_other -> other_design
      let fileKey = designKey.replace("design_", "");

      // Handle special cases
      if (fileKey.startsWith("bottom_of_")) {
        fileKey = fileKey.replace("bottom_of_", "bottom_");
      } else if (fileKey === "other") {
        fileKey = "other_design";
      }

      const svdSvlKey = `${fileKey}_svd_svl`;

      console.log("🔍 SVD/SVL Key check:", {
        fileKey,
        svdSvlKey,
        isExcluded: excludedFileTypes[svdSvlKey],
      });

      // Remove from excluded file types to make it required
      if (excludedFileTypes[svdSvlKey]) {
        const newExcludedFileTypes = { ...excludedFileTypes };
        delete newExcludedFileTypes[svdSvlKey];

        console.log("🔄 Auto-enabling svd/svl for design:", {
          designKey,
          fileKey,
          svdSvlKey,
        });
        setExcludedFileTypes(newExcludedFileTypes);
      }
    }

    setFormData(updated);
    debouncedAutoSave(updated, true);

    // Trigger debounced email generation
    debouncedEmailGeneration();
  };

  const handleFileChange = (designKey, fileType, files) => {
    const fieldName = `${designKey}_${fileType}`;
    const updated = {
      ...formData,
      [fieldName]: files,
      excluded_file_types: excludedFileTypes, // Preserve excluded file types
    };
    setFormData(updated);

    // CRITICAL: Update formDataRef immediately so email generation has latest data
    formDataRef.current = updated;

    debouncedAutoSave(updated, true);

    // Trigger debounced email generation when files are uploaded
    console.log(
      `📁 File upload detected for ${fieldName}, triggering email generation in 2 seconds...`,
    );
    debouncedEmailGeneration();
  };

  // Update formData when exportData changes (e.g., when discrepancy is added)
  useEffect(() => {
    if (exportData && !isCreating && exportData.related_discrepancy_ids) {
      setFormData((prev) => {
        const updated = {
          ...prev,
          // Convert string IDs to numbers for consistent type handling
          related_discrepancy_ids: (
            exportData.related_discrepancy_ids || []
          ).map((id) => (typeof id === "string" ? parseInt(id, 10) : id)),
        };
        // Note: Removed autosave to prevent infinite loop
        // The form will be saved when user makes actual changes
        return updated;
      });
    }
  }, [exportData?.related_discrepancy_ids, isCreating]);

  // Helper functions for discrepancy colors
  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "#6c757d",
      Clarifying: "#ffc107",
      "Pending Response": "#17a2b8",
      "RFI Sent": "#007bff",
      Resolved: "#28a745",
      "On Hold": "#fd7e14",
      "No Response": "#dc3545",
    };
    return statusColors[status] || "#6c757d";
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      Low: "#28a745",
      Medium: "#ffc107",
      High: "#fd7e14",
      Critical: "#dc3545",
    };
    return priorityColors[priority] || "#6c757d";
  };

  // Handle saving edited discrepancy
  const handleSaveDiscrepancy = async (updatedDiscrepancy) => {
    console.log("handleSaveDiscrepancy called with:", {
      discrepancyId: editingDiscrepancy?.id,
      editingDiscrepancy: editingDiscrepancy,
      updatedData: updatedDiscrepancy,
    });

    try {
      if (!editingDiscrepancy?.id) {
        throw new Error("No discrepancy ID found for save");
      }

      // Update the discrepancy in the database
      const savedDiscrepancy = await DiscrepancyService.updateDiscrepancy(
        editingDiscrepancy.id,
        updatedDiscrepancy,
      );

      console.log("Discrepancy saved successfully:", savedDiscrepancy);

      // Reload the project discrepancies to get the updated data
      await loadProjectDiscrepancies();

      // Close the modal after successful save
      setShowEditDiscrepancyModal(false);
      setEditingDiscrepancy(null);

      // Return the saved data
      return savedDiscrepancy;
    } catch (error) {
      console.error("Error saving discrepancy:", error);
      throw error; // Let the modal handle the error
    }
  };

  // Handle creating new discrepancy
  const handleCreateDiscrepancy = async (discrepancyData, closeModal) => {
    console.log("📝 handleCreateDiscrepancy called with:", discrepancyData);

    try {
      // Create the discrepancy in the database
      const savedDiscrepancy = await DiscrepancyService.createDiscrepancy(
        discrepancyData,
        window.location.origin,
      );

      console.log("✅ Discrepancy created successfully:", savedDiscrepancy);
      console.log("📋 Saved discrepancy ID:", savedDiscrepancy?.id);

      // Reload the project discrepancies to include the new one
      console.log("🔄 Reloading project discrepancies...");
      await loadProjectDiscrepancies();
      console.log(
        "✅ Discrepancies reloaded. Count:",
        projectDiscrepancies.length,
      );

      // Auto-attach the new discrepancy to the current export if we have an export ID
      console.log("🔗 Checking auto-attach conditions:", {
        hasExportId: !!exportData?.id,
        exportId: exportData?.id,
        hasSavedDiscrepancyId: !!savedDiscrepancy?.id,
        savedDiscrepancyId: savedDiscrepancy?.id,
      });

      if (exportData?.id && savedDiscrepancy?.id) {
        const currentRelatedIds = formData.related_discrepancy_ids || [];
        const updatedRelatedIds = [...currentRelatedIds, savedDiscrepancy.id];
        console.log("🔗 Auto-attaching discrepancy:", {
          currentIds: currentRelatedIds,
          newId: savedDiscrepancy.id,
          updatedIds: updatedRelatedIds,
        });

        const updated = {
          ...formData,
          related_discrepancy_ids: updatedRelatedIds,
        };
        setFormData(updated);
        await debouncedAutoSave(updated, true);
        console.log("✅ Discrepancy auto-attached and saved");
      } else {
        console.log("⚠️ NOT auto-attaching - conditions not met");
      }

      return savedDiscrepancy;
    } catch (error) {
      console.error("❌ Error creating discrepancy:", error);
      throw error; // Let the modal handle the error
    }
  };

  // Handle deleting discrepancy
  const handleDeleteDiscrepancy = async (discrepancyId) => {
    console.log("🗑️ handleDeleteDiscrepancy called for ID:", discrepancyId);
    try {
      // Delete the discrepancy
      console.log("🗑️ Deleting discrepancy from database...");
      await DiscrepancyService.deleteDiscrepancy(discrepancyId);
      console.log("✅ Discrepancy deleted successfully from database");

      // Remove from selected list if it was selected
      const updatedIds = formData.related_discrepancy_ids.filter(
        (id) => id !== discrepancyId,
      );

      // Update form data if the deleted discrepancy was selected
      if (updatedIds.length !== formData.related_discrepancy_ids.length) {
        console.log("🔄 Removing discrepancy from selected list...");
        const updated = {
          ...formData,
          related_discrepancy_ids: updatedIds,
        };
        setFormData(updated);
        debouncedAutoSave(updated, true);
      }

      // Reload the project discrepancies
      console.log("🔄 Reloading project discrepancies list...");
      await loadProjectDiscrepancies();
      console.log("✅ Project discrepancies reloaded successfully");

      // Don't close the modal here - let the DiscrepancyModal component handle that
      // The modal will close itself after successful delete via its onClose callback
    } catch (error) {
      console.error("❌ Error deleting discrepancy:", error);
      throw error; // Let the modal handle the error
    }
  };

  // Handle bulk download of all files
  const handleBulkDownload = async () => {
    const selectedDesigns = getSelectedDesignTypes();

    if (selectedDesigns.length === 0) {
      alert("No design types selected for download.");
      return;
    }

    // Set loading state
    setBulkDownloading(true);

    try {
      // Prepare design files data structure for the new createZipDownload function
      const designFilesData = {};

      selectedDesigns.forEach((design) => {
        const designFiles = {
          topcon_files: formData[`${design.key}_topcon_files`] || [],
          leica_files: formData[`${design.key}_leica_files`] || [],
          vcl_files: formData[`${design.key}_vcl_files`] || [],
          dxf_ttm_files: formData[`${design.key}_dxf_ttm_files`] || [],
          svd_svl_files: formData[`${design.key}_svd_svl_files`] || [],
        };

        // Only include designs that have files
        const hasFiles = Object.values(designFiles).some(
          (files) => files.length > 0,
        );
        if (hasFiles) {
          designFilesData[design.name] = {
            designType: design.key,
            files: designFiles,
          };
        }
      });

      // Add background images as shared files if they exist
      if (
        formData.background_image_files &&
        formData.background_image_files.length > 0
      ) {
        designFilesData.background_images = {
          designType: "background",
          files: {
            background_image_files: formData.background_image_files,
          },
        };
      }

      // Add control CSV files if they exist
      if (formData.control_csv_files && formData.control_csv_files.length > 0) {
        designFilesData.control_csv = {
          designType: "control_csv",
          files: {
            control_csv_files: formData.control_csv_files,
          },
        };
      }

      // Add TBC files if they exist
      if (formData.tbc_files && formData.tbc_files.length > 0) {
        designFilesData.tbc = {
          designType: "tbc",
          files: {
            tbc_files: formData.tbc_files,
          },
        };
      }

      if (Object.keys(designFilesData).length === 0) {
        alert("No files uploaded to download.");
        setBulkDownloading(false);
        return;
      }

      console.log("Design files data for bulk download:", designFilesData);

      // Get project and client IDs
      const projectId = project?.id || "default";
      const clientId = project?.client_id || "default";

      const result = await createAndUploadZip(
        designFilesData,
        project?.projectName || project?.name || "Export",
        projectId,
        clientId,
        formData.export_date,
      );

      if (result.success) {
        // Save the download link to formData
        const updatedFormData = {
          ...formData,
          download_link: result.url,
        };
        setFormData(updatedFormData);

        // Auto-save with the download link
        if (!isCreating && exportData?.id) {
          try {
            await onSave(exportData.id, updatedFormData);
            console.log("Saved download_link to export");
          } catch (error) {
            console.error("Error saving download_link:", error);
          }
        }

        // Create download link
        const a = document.createElement("a");
        a.href = result.url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clear loading state before alert
        setBulkDownloading(false);

        alert(`Download complete: ${result.filename}`);
      } else {
        // Clear loading state before alert
        setBulkDownloading(false);
        alert(`Download failed: ${result.error}`);
      }
    } catch (error) {
      console.error("Bulk download error:", error);
      // Clear loading state before alert
      setBulkDownloading(false);
      alert(
        "Failed to create bulk download. Please try downloading files individually.",
      );
    }
  };

  // Generate bulk download URL for email preview (without triggering download)
  const generateBulkDownloadUrlForEmail = async () => {
    // Check if we're already generating a URL to prevent duplicate calls
    if (window._generatingEmailUrl) {
      console.log("📦 Already generating email URL, skipping duplicate call");
      return null;
    }

    window._generatingEmailUrl = true;
    const timestamp = new Date().toISOString();
    console.log(
      `📦 [${timestamp}] Starting bulk download URL generation for email`,
    );

    try {
      const selectedDesigns = getSelectedDesignTypes();
      console.log(`📦 Selected design types:`, selectedDesigns);

      if (selectedDesigns.length === 0) {
        console.warn(
          "📦 No design types selected - cannot generate download link",
        );
        window._generatingEmailUrl = false;
        return null;
      }

      // Prepare design files data structure
      const designFilesData = {};
      let totalFileCount = 0;

      selectedDesigns.forEach((design) => {
        const designFiles = {
          topcon_files: formData[`${design.key}_topcon_files`] || [],
          leica_files: formData[`${design.key}_leica_files`] || [],
          vcl_files: formData[`${design.key}_vcl_files`] || [],
          dxf_ttm_files: formData[`${design.key}_dxf_ttm_files`] || [],
          svd_svl_files: formData[`${design.key}_svd_svl_files`] || [],
        };

        // Count files for this design
        const fileCount = Object.values(designFiles).reduce(
          (sum, files) => sum + files.length,
          0,
        );

        // Only include designs that have files
        if (fileCount > 0) {
          designFilesData[design.name] = {
            designType: design.key,
            files: designFiles,
          };
          totalFileCount += fileCount;
          console.log(`📦 Design "${design.name}" has ${fileCount} files`);
        }
      });

      // Add background images as shared files if they exist
      if (
        formData.background_image_files &&
        formData.background_image_files.length > 0
      ) {
        designFilesData.background_images = {
          designType: "background",
          files: {
            background_image_files: formData.background_image_files,
          },
        };
        totalFileCount += formData.background_image_files.length;
        console.log(
          `📦 Background images: ${formData.background_image_files.length} files`,
        );
      }

      // Add control CSV files if they exist
      if (formData.control_csv_files && formData.control_csv_files.length > 0) {
        designFilesData.control_csv = {
          designType: "control_csv",
          files: {
            control_csv_files: formData.control_csv_files,
          },
        };
        totalFileCount += formData.control_csv_files.length;
        console.log(
          `📦 Control CSV: ${formData.control_csv_files.length} files`,
        );
      }

      // Add TBC files if they exist
      if (formData.tbc_files && formData.tbc_files.length > 0) {
        designFilesData.tbc = {
          designType: "tbc",
          files: {
            tbc_files: formData.tbc_files,
          },
        };
        totalFileCount += formData.tbc_files.length;
        console.log(`📦 TBC files: ${formData.tbc_files.length} files`);
      }

      if (Object.keys(designFilesData).length === 0) {
        console.warn(
          "📦 No files found in any design type - cannot generate download link",
        );
        alert(
          "Cannot generate download link: No files have been uploaded. Please upload files before sending.",
        );
        window._generatingEmailUrl = false;
        return null;
      }

      console.log(`📦 Total files to package: ${totalFileCount}`);
      console.log(`📦 Design files data structure:`, designFilesData);

      // Get project and client IDs
      const projectId = project?.id || "default";
      const clientId = project?.client_id || "default";
      const projectName = project?.projectName || project?.name || "Export";

      console.log(
        `📦 Creating zip for project: ${projectName} (ID: ${projectId}, Client: ${clientId})`,
      );

      const result = await createAndUploadZip(
        designFilesData,
        projectName,
        projectId,
        clientId,
        formData.export_date,
      );

      console.log(`📦 Zip creation result:`, result);

      if (result.success) {
        console.log(`✅ Successfully created download link: ${result.url}`);

        // Save the download link to formData
        const updatedFormData = {
          ...formData,
          download_link: result.url,
        };
        setFormData(updatedFormData);

        // Auto-save with the download link
        if (!isCreating && exportData?.id) {
          try {
            await onSave(exportData.id, updatedFormData);
            console.log("✅ Saved download_link to database");
          } catch (error) {
            console.error("❌ Error saving download_link to database:", error);
          }
        }

        window._generatingEmailUrl = false;
        return {
          url: result.url,
          filename: result.filename,
          filePath: result.filePath,
        };
      } else {
        console.error(`❌ Failed to create zip:`, result.error);
        alert(
          `Failed to generate download link: ${result.error || "Unknown error"}. Please try again or contact support.`,
        );
      }

      window._generatingEmailUrl = false;
      return null;
    } catch (error) {
      console.error("❌ Error generating bulk download URL for email:", error);
      console.error("Error stack:", error.stack);
      alert(
        `Error generating download link: ${error.message}. Please try again or contact support.`,
      );
      window._generatingEmailUrl = false;
      return null;
    }
  };

  // Generate email content
  const handleGenerateEmail = useCallback(async () => {
    setGeneratingEmail(true);
    try {
      // Use latest formData from ref
      const currentFormData = formDataRef.current;

      // First, save the current file_formats_needed to the database
      if (!isCreating && exportData?.id) {
        try {
          // Include excluded_file_types in the save
          const dataWithExclusions = {
            ...currentFormData,
            excluded_file_types: excludedFileTypes,
          };
          await onSave(exportData.id, dataWithExclusions);
          console.log("Saved current file_formats_needed to database");
        } catch (error) {
          console.error(
            "Error saving export data before email generation:",
            error,
          );
        }
      }

      // Build model types list from design types
      let modelTypes = [];
      if (currentFormData.design_finish_grade) modelTypes.push("Finish Grade");
      if (currentFormData.design_rough_grade) modelTypes.push("Rough Grade");
      if (currentFormData.design_erosion_phase_1)
        modelTypes.push("Erosion Control Phase 1");
      if (currentFormData.design_erosion_phase_2)
        modelTypes.push("Erosion Control Phase 2");
      if (currentFormData.design_bottom_of_storm)
        modelTypes.push("Bottom of Storm");
      if (currentFormData.design_bottom_of_water)
        modelTypes.push("Bottom of Water");
      if (currentFormData.design_bottom_of_sanitary)
        modelTypes.push("Bottom of Sanitary");

      // Generate email subject with model types
      let emailSubject = `${project?.projectName || project?.name}`;

      if (modelTypes.length === 0) {
        emailSubject += " Model";
      } else if (modelTypes.length === 1) {
        emailSubject += ` ${modelTypes[0]} Model`;
      } else if (modelTypes.length === 2) {
        emailSubject += ` ${modelTypes[0]} and ${modelTypes[1]} Models`;
      } else {
        // For 3+ models, use proper comma formatting with "and" before last item
        const lastModel = modelTypes[modelTypes.length - 1];
        const otherModels = modelTypes.slice(0, -1);
        emailSubject += ` ${otherModels.join(", ")}, and ${lastModel} Models`;
      }

      // Get contact first name from primary recipient or project contact
      let contactFirstName = "Hi";

      // Try to get name from primary email recipient first
      if (currentFormData.email_to && currentFormData.email_to.length > 0) {
        const primaryRecipientId = currentFormData.email_to[0];
        const primaryContact = clientContacts.find(
          (c) => c.id === primaryRecipientId,
        );
        if (primaryContact?.name) {
          contactFirstName =
            primaryContact.name.split(" ")[0] || primaryContact.name;
        }
      }

      // Fallback to project contact if no recipient name found
      if (contactFirstName === "Hi" && projectContact?.name) {
        contactFirstName =
          projectContact.name.split(" ")[0] || projectContact.name;
      }

      // Build structured data for new email builder (Option A: Hybrid Approach)
      const greeting = `${contactFirstName},`;
      const actualFormats = getActualIncludedFormats(currentFormData);

      // Build discrepancies array with structured data
      const structuredDiscrepancies = [];
      const allowedTypes = [
        "Grading Clarification",
        "Utility Discrepancy",
        "Profile Discrepancy",
      ];

      let hasAllowedDiscrepancies = false;

      if (
        currentFormData.related_discrepancy_ids &&
        currentFormData.related_discrepancy_ids.length > 0
      ) {
        const selectedDiscrepancies = projectDiscrepancies.filter((d) =>
          currentFormData.related_discrepancy_ids.includes(d.id),
        );

        selectedDiscrepancies.forEach((discrepancy) => {
          const discType =
            discrepancy.discrepancyType || discrepancy.type || "Discrepancy";

          // Track discrepancy types for conditional footer messages
          if (allowedTypes.includes(discType)) {
            hasAllowedDiscrepancies = true;
          }

          // Extract video URL (loomLink)
          const videoUrl =
            discrepancy.loomLink || discrepancy.loom_link || null;

          // Extract file URLs from markup_attachment (can be single object or array)
          const fileLinks = [];
          const attachment =
            discrepancy.markupAttachment || discrepancy.markup_attachment;

          if (attachment) {
            // Handle array of attachments
            if (Array.isArray(attachment)) {
              attachment.forEach((att) => {
                if (att && att.url && att.filename) {
                  fileLinks.push({ url: att.url, filename: att.filename });
                }
              });
            }
            // Handle single attachment object
            else if (
              typeof attachment === "object" &&
              attachment.url &&
              attachment.filename
            ) {
              fileLinks.push({
                url: attachment.url,
                filename: attachment.filename,
              });
            }
          }

          // Debug logging
          console.log("🔍 STRUCTURED DISCREPANCY:", {
            id: discrepancy.id,
            type: discType,
            videoUrl,
            fileLinks,
            attachment,
          });

          structuredDiscrepancies.push({
            type: discType,
            videoUrl,
            fileLinks, // Array of {url, filename} objects
          });
        });
      }

      // Build conditional footer messages
      const footerMessages = [];
      if (hasAllowedDiscrepancies) {
        footerMessages.push(
          "Additionally, see the information above regarding some discrepancies we encountered when building the model. Please review and let me know if you have any questions or need any additional information.",
        );
      } else {
        // No discrepancies attached - use default message
        footerMessages.push("Please let me know if you have any questions.");
      }

      // Auto-select project contact as email recipient if not already selected
      let updatedEmailTo = currentFormData.email_to || [];
      if (projectContact && projectContact.id) {
        // Check if project contact is not already selected
        if (!updatedEmailTo.includes(projectContact.id)) {
          updatedEmailTo = [...updatedEmailTo, projectContact.id];
        }
      }

      // Generate HTML using structured email builder (Option A: Hybrid Approach)
      const htmlContent = buildStructuredEmail({
        greeting,
        downloadPlaceholder: DOWNLOAD_PLACEHOLDER,
        downloadUrl: null, // Will be filled in when sending
        includedFormats: actualFormats,
        discrepancies: structuredDiscrepancies,
        customMessage: currentFormData.custom_message || "", // User's custom additions
        footerMessages,
        signature: currentUser?.email_signature_html || "",
      });

      const updatedFormData = {
        ...currentFormData,
        email_html: htmlContent,
        email_subject: emailSubject,
        email_to: updatedEmailTo,
        // Don't store email_body anymore - it's generated from structured data
      };

      console.log(
        "🔍 EMAIL GENERATION - About to save email_to:",
        updatedEmailTo,
      );
      console.log(
        "🔍 EMAIL GENERATION - Full updatedFormData.email_to:",
        updatedFormData.email_to,
      );

      setFormData(updatedFormData);

      // Trigger autosave for the email content
      debouncedAutoSave(updatedFormData, true); // Immediate save for generated content
    } catch (error) {
      console.error("Error generating email:", error);
      setToast({
        visible: true,
        message: "Failed to generate email content",
        type: "error",
      });
      setTimeout(() => {
        setToast({ visible: false, message: "", type: "error" });
      }, 3000);
    } finally {
      setGeneratingEmail(false);
    }
  }, [
    isCreating,
    exportData?.id,
    project,
    projectContact,
    projectDiscrepancies,
    currentUser,
    clientContacts,
    onSave,
    debouncedAutoSave,
    getActualIncludedFormats,
    formatEmailToHTML,
    DOWNLOAD_PLACEHOLDER,
    excludedFileTypes,
  ]);

  // Debounced email generation function (2 seconds)
  const debouncedEmailGeneration = useCallback(() => {
    // Clear any existing timer
    if (emailGenerationTimerRef.current) {
      clearTimeout(emailGenerationTimerRef.current);
    }

    console.log("⏱️ Email generation timer set (2 second delay)...");

    // Set new timer for 2 seconds
    emailGenerationTimerRef.current = setTimeout(() => {
      // Only generate if we're not already generating and we have content to work with
      if (!generatingEmail && exportData?.id) {
        console.log("🔄 Auto-generating email after user changes...");
        handleGenerateEmail();
      } else {
        console.log("⚠️ Email generation skipped:", {
          generatingEmail,
          hasExportId: !!exportData?.id,
          isCreating,
        });
      }
    }, 2000);
  }, [generatingEmail, exportData?.id, handleGenerateEmail, isCreating]);

  // Mark export as sent without sending email
  const handleMarkAsSent = async () => {
    if (
      !window.confirm(
        "Mark this export as sent? This will remove it from the draft/pending queue.",
      )
    ) {
      return;
    }

    setMarkingAsSent(true);
    try {
      const now = new Date().toISOString();
      const updates = {
        status: "sent",
        sent_date: now,
      };

      // Update in database through the onSave callback
      await onSave(exportData.id, updates);

      // Update local form data
      setFormData((prev) => ({
        ...prev,
        status: "sent",
      }));

      setSaveStatus("saved");
      setLastSaved(new Date());

      // Show success message
      showToast("Export marked as sent successfully", "success");
    } catch (error) {
      console.error("Error marking export as sent:", error);
      showToast("Failed to mark export as sent. Please try again.", "error");
    } finally {
      setMarkingAsSent(false);
    }
  };

  // Send export email
  const handleSendEmail = async () => {
    console.log("📧 [ExportForm] handleSendEmail clicked");

    // FIRST: Validate required files before anything else
    const fileValidation = validateRequiredFiles();
    console.log("File validation result:", fileValidation);
    if (!fileValidation.isValid) {
      // Show alert with missing files
      const missingFilesList = fileValidation.missingFields
        .map((f) => f.label)
        .join("\n• ");
      alert(
        `Please upload the following required files before sending:\n\n• ${missingFilesList}`,
      );

      // Scroll to first missing field AFTER alert is dismissed
      setTimeout(() => {
        if (fileValidation.missingFields.length > 0) {
          const firstMissing = fileValidation.missingFields[0];
          const element = document.getElementById(firstMissing.id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 100); // Small delay to ensure alert is closed

      return;
    }

    // SECOND: Check for incomplete subitems
    const incompleteCount = getIncompleteSubitemsCount();
    if (incompleteCount > 0) {
      setWarningModalOpen(true);
      return;
    }

    proceedWithSend();
  };

  const handleOverrideSend = () => {
    setWarningModalOpen(false);
    proceedWithSend();
  };

  const proceedWithSend = async () => {
    console.log("🚀 [ExportForm] proceedWithSend called");
    console.log("Current formData:", {
      email_to: formData.email_to,
      email_cc: formData.email_cc,
      email_subject: formData.email_subject,
      email_body_length: formData.email_body?.length || 0,
      export_id: exportData?.id,
    });

    // Check if email_to is selected
    if (!formData.email_to || formData.email_to.length === 0) {
      console.log("❌ No recipients selected");
      alert("Please select at least one recipient.");
      return;
    }

    // Auto-generate email if email_html is empty
    if (!formData.email_html || formData.email_html.trim() === "") {
      console.log("Email HTML is empty, auto-generating email content...");
      try {
        await handleGenerateEmail();
        // Wait for state to update after generation
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error("Error generating email:", error);
        alert(
          "Failed to generate email content. Please try generating the email manually first.",
        );
        return;
      }
    }

    // Check if we need to create the export first
    let exportIdToUse = exportData?.id;

    if (!exportIdToUse) {
      console.log("📝 No export ID yet - creating new export first...");

      // For new exports, we need to create it first
      if (isCreating) {
        try {
          const newExportData = {
            ...formData,
            project_id: project.id,
            status: "draft",
            created_by: currentUser?.id,
          };

          console.log("Creating new export with data:", newExportData);
          const newExport = await onSave(newExportData);

          if (!newExport || !newExport.id) {
            console.error("❌ Failed to create export or no ID returned");
            alert("Failed to create export. Please try saving manually first.");
            return;
          }

          exportIdToUse = newExport.id;
          console.log("✅ New export created with ID:", exportIdToUse);

          // Wait a moment to ensure database transaction is committed
          console.log("⏳ Waiting for database transaction to commit...");
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Verify the export exists in database before proceeding
          console.log("🔍 Verifying export exists in database...");
          const { data: verifyExport, error: verifyError } = await supabase
            .from("export_and_send")
            .select("id, project_id, status")
            .eq("id", exportIdToUse)
            .single();

          if (verifyError || !verifyExport) {
            console.error("❌ Export verification failed!", {
              exportId: exportIdToUse,
              error: verifyError,
            });
            alert(
              "Export was created but cannot be found in database. Please try again.",
            );
            return;
          }

          console.log("✅ Export confirmed in database:", verifyExport);

          // Update local exportData reference
          exportData = newExport;
        } catch (error) {
          console.error("❌ Error creating export:", error);
          alert("Failed to create export. Please try saving manually first.");
          return;
        }
      } else {
        console.error("❌ No export ID and not in creating mode");
        alert("Please save the export first before sending email.");
        return;
      }
    } else {
      console.log("✅ Using existing export ID:", exportIdToUse);
    }

    console.log("Export data object:", exportData);

    try {
      setSaving(true);
      setSendingEmail(true);
      console.log("Starting export email send process...");
      console.log("Export ID to use:", exportIdToUse);
      console.log("Export ID type:", typeof exportIdToUse);
      console.log("Email recipients:", formData.email_to);
      console.log("Email CC:", formData.email_cc);
      console.log("Email subject:", formData.email_subject);

      // Generate download URL for sending
      let downloadUrl = formData.download_link; // Use existing if available

      console.log("Generating download link for email send...");
      try {
        const bulkDownloadUrl = await generateBulkDownloadUrlForEmail();
        if (bulkDownloadUrl) {
          downloadUrl = bulkDownloadUrl.url;
          console.log("Successfully generated download URL:", downloadUrl);
        }
      } catch (error) {
        console.warn("Failed to generate download link:", error);
      }

      // Regenerate HTML email with actual download URL (using structured builder)
      console.log("Generating final HTML with download URL...");

      // Get contact first name for greeting
      let contactFirstName = "Hi";
      if (formData.email_to && formData.email_to.length > 0) {
        const primaryRecipientId = formData.email_to[0];
        const primaryContact = clientContacts.find(
          (c) => c.id === primaryRecipientId,
        );
        if (primaryContact?.name) {
          contactFirstName =
            primaryContact.name.split(" ")[0] || primaryContact.name;
        }
      } else if (projectContact?.name) {
        contactFirstName =
          projectContact.name.split(" ")[0] || projectContact.name;
      }

      const greeting = `${contactFirstName},`;
      const actualFormats = getActualIncludedFormats(formData);

      // Build discrepancies array
      const structuredDiscrepancies = [];
      const allowedTypes = [
        "Grading Clarification",
        "Utility Discrepancy",
        "Profile Discrepancy",
      ];
      let hasAllowedDiscrepancies = false;

      if (
        formData.related_discrepancy_ids &&
        formData.related_discrepancy_ids.length > 0
      ) {
        const selectedDiscrepancies = projectDiscrepancies.filter((d) =>
          formData.related_discrepancy_ids.includes(d.id),
        );

        selectedDiscrepancies.forEach((discrepancy) => {
          const discType =
            discrepancy.discrepancyType || discrepancy.type || "Discrepancy";

          if (allowedTypes.includes(discType)) {
            hasAllowedDiscrepancies = true;
          }

          const videoUrl =
            discrepancy.loomLink || discrepancy.loom_link || null;

          const fileLinks = [];
          const attachment =
            discrepancy.markupAttachment || discrepancy.markup_attachment;

          if (attachment) {
            if (Array.isArray(attachment)) {
              attachment.forEach((att) => {
                if (att && att.url && att.filename) {
                  fileLinks.push({ url: att.url, filename: att.filename });
                }
              });
            } else if (
              typeof attachment === "object" &&
              attachment.url &&
              attachment.filename
            ) {
              fileLinks.push({
                url: attachment.url,
                filename: attachment.filename,
              });
            }
          }

          structuredDiscrepancies.push({
            type: discType,
            videoUrl,
            fileLinks,
          });
        });
      }

      // Build footer messages
      const footerMessages = [];
      if (hasAllowedDiscrepancies) {
        footerMessages.push(
          "Additionally, see the information above regarding some discrepancies we encountered when building the model. Please review and let me know if you have any questions or need any additional information.",
        );
      } else {
        footerMessages.push("Please let me know if you have any questions.");
      }

      // Generate HTML using structured email builder with actual download URL
      const finalHtmlContent = buildStructuredEmail({
        greeting,
        downloadPlaceholder: DOWNLOAD_PLACEHOLDER,
        downloadUrl: downloadUrl, // Use actual download URL
        includedFormats: actualFormats,
        discrepancies: structuredDiscrepancies,
        customMessage: formData.custom_message || "",
        footerMessages,
        signature: currentUser?.email_signature_html || "",
      });

      // Step 2: Save the HTML and email content to database FIRST (without triggering send)
      console.log("Saving HTML and email content to database...");
      const preUpdateData = {
        ...formData,
        email_html: finalHtmlContent, // HTML with the download link properly formatted
        download_link: downloadUrl,
        email_to: formData.email_to,
        email_cc: formData.email_cc,
        email_subject: formData.email_subject,
        custom_message: formData.custom_message || "",
      };

      // Save all email data EXCEPT send_email flag
      await onSave(exportIdToUse, preUpdateData);
      console.log("Email content and HTML saved successfully");

      // Step 3: NOW trigger the email send (n8n will get the updated HTML)
      console.log("Triggering email send with updated HTML...");

      // Prepare user info for sent_by field - structure for n8n to use
      const sentBy = currentUser
        ? {
            user_id: currentUser.id,
            user_name: currentUser.name,
            user_email: currentUser.email,
          }
        : {
            user_id: null,
            user_name: "Unknown User",
            user_email: null,
          };

      // Final verification before sending email
      console.log(
        "🔍 Final verification: checking export exists before sending...",
      );
      const { data: finalCheck, error: finalCheckError } = await supabase
        .from("export_and_send")
        .select("id, status, send_email")
        .eq("id", exportIdToUse)
        .single();

      if (finalCheckError || !finalCheck) {
        console.error("❌ Export missing before send!", {
          exportId: exportIdToUse,
          error: finalCheckError,
        });
        alert("Export record not found. Please save the export and try again.");
        return;
      }

      console.log("✅ Export confirmed before send:", finalCheck);

      // Use workaround method to avoid 100 arguments error
      console.log("📨 About to call sendExportEmailWorkaround with:", {
        exportId: exportIdToUse,
        hasEmailHTML: !!finalHtmlContent,
        emailTo: formData.email_to,
        emailCc: formData.email_cc,
        emailSubject: formData.email_subject,
      });

      await ExportAndSendService.sendExportEmailWorkaround(
        exportIdToUse, // Use the ID we verified/created
        "", // Email body not used anymore (HTML is already in database)
        formData.email_to,
        formData.email_cc,
        formData.email_subject,
        sentBy,
        downloadUrl, // Pass the download URL
      );

      console.log("Database updated with send_email = true");
      console.log(
        "n8n will now receive the export with updated HTML containing download link",
      );

      // Update local state to reflect sent status
      const updatedFormData = {
        ...preUpdateData,
        status: "sent",
      };
      setFormData(updatedFormData);

      // Update attached discrepancies to "Sent" status
      if (
        formData.related_discrepancy_ids &&
        formData.related_discrepancy_ids.length > 0
      ) {
        console.log(
          "Updating attached discrepancies to 'Sent' status:",
          formData.related_discrepancy_ids,
        );
        try {
          // Update all attached discrepancies to "Sent" status
          const discrepancyUpdatePromises =
            formData.related_discrepancy_ids.map((discrepancyId) =>
              DiscrepancyService.updateDiscrepancy(discrepancyId, {
                discrepancyStatus: "Sent", // Use camelCase field name
                sent_date: new Date().toISOString(),
              }),
            );
          await Promise.all(discrepancyUpdatePromises);
          console.log(
            "Successfully updated all attached discrepancies to 'Sent' status",
          );

          // Reload discrepancies to show updated status
          await loadProjectDiscrepancies();
        } catch (discrepancyUpdateError) {
          console.error(
            "Error updating attached discrepancies:",
            discrepancyUpdateError,
          );
          // Don't fail the entire operation if discrepancy updates fail
        }
      }

      // Update project fields if specified
      const projectUpdates = {};
      if (readyToInvoice !== null) {
        projectUpdates.ready_to_invoice = readyToInvoice;
      }
      if (completeProject === true) {
        projectUpdates.modeling_status = "Completed";
      }

      if (Object.keys(projectUpdates).length > 0) {
        console.log("Updating project with:", projectUpdates);
        try {
          const { error: projectUpdateError } = await supabase
            .from("projects")
            .update(projectUpdates)
            .eq("id", project.id);

          if (projectUpdateError) {
            console.error("Error updating project:", projectUpdateError);
            // Don't fail the entire operation if project update fails
          } else {
            console.log("Successfully updated project");
          }
        } catch (projectUpdateError) {
          console.error("Error updating project:", projectUpdateError);
          // Don't fail the entire operation if project update fails
        }
      }

      showToast(
        "Export email sent successfully! The email will be processed by our automation system.",
        "success",
      );

      // Call onSuccess callback if provided (to refresh drafts list and close modal)
      if (onSuccess) {
        onSuccess();
      } else if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("❌ Error sending export email:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        stack: error.stack,
      });

      // More specific error messages with better diagnostics
      let errorMessage = "Failed to send email.";

      // Check if this might be a trigger issue
      if (
        error.message?.includes("trigger failed") ||
        error.message?.includes("queue entry not created")
      ) {
        errorMessage =
          "Database trigger issue detected. Please run the SQL script to enable export triggers:\n\nsql/check_and_enable_export_triggers.sql";
      }
      let diagnosticInfo = "";

      if (error.message?.includes("Database trigger failed")) {
        errorMessage += " The database trigger is not working properly.";
        diagnosticInfo =
          "\n\nDIAGNOSTIC INFO:\n" +
          "• The export was saved to the database\n" +
          "• But the email queue trigger did not fire\n" +
          "• This means n8n will not receive the notification\n" +
          "• Contact your system administrator to fix the database trigger";
      } else if (error.message?.includes("queue entry not created")) {
        errorMessage += " Email queuing system is not working.";
        diagnosticInfo =
          "\n\nDIAGNOSTIC INFO:\n" +
          "• Database connection is working\n" +
          "• But the email trigger is missing or disabled\n" +
          "• Check database triggers in Supabase dashboard";
      } else if (
        error.message?.includes("network") ||
        error.message?.includes("fetch")
      ) {
        errorMessage += " Network error - please check your connection.";
      } else if (
        error.message?.includes("permission") ||
        error.message?.includes("unauthorized")
      ) {
        errorMessage += " Permission denied - please contact support.";
      } else if (
        error.message?.includes("port") ||
        error.message?.includes("connection")
      ) {
        errorMessage +=
          " Connection error - the email service may be unavailable.";
      } else if (error.code === "PGRST116") {
        errorMessage += " Database query error - please try again.";
        diagnosticInfo =
          "\n\nDIAGNOSTIC INFO:\n" +
          "• This is a database-level error\n" +
          "• Check the browser console for more details\n" +
          "• The export data may not have been saved properly";
      } else if (error.message) {
        errorMessage += ` Error: ${error.message}`;

        // Add diagnostic info for common error patterns
        if (error.message.toLowerCase().includes("bigint")) {
          diagnosticInfo =
            "\n\nDIAGNOSTIC INFO:\n" +
            "• This is a database ID conversion error\n" +
            "• The export may have been created but email sending failed";
        }
      }

      // Show detailed error message
      if (diagnosticInfo) {
        // Use a more detailed display for technical errors
        if (
          window.confirm(
            errorMessage +
              diagnosticInfo +
              "\n\nWould you like to copy this error information to clipboard?",
          )
        ) {
          navigator.clipboard?.writeText(
            `Export Email Error:\n${errorMessage}\n${error.message}\n${diagnosticInfo}\n\nTimestamp: ${new Date().toISOString()}`,
          );
        }
      } else {
        alert(errorMessage);
      }
    } finally {
      setSaving(false);
      setSendingEmail(false);
    }
  };

  const handleFileFormatsChange = (selectedFormats) => {
    const updatedFormData = {
      ...formData,
      file_formats_needed: selectedFormats,
    };
    setFormData(updatedFormData);

    // Autosave when file formats change
    autoSave(updatedFormData);

    // Trigger debounced email generation
    debouncedEmailGeneration();
  };

  const shouldShowFileUpload = (fileType) => {
    // Use the export-specific file formats
    const relevantFormats = formData.file_formats_needed || [];

    if (fileType === "topcon") {
      return relevantFormats?.includes("Topcon");
    }
    if (fileType === "leica") {
      return relevantFormats?.includes("Leica");
    }
    if (["vcl", "dxf_ttm", "background_image"].includes(fileType)) {
      // Show VCL, DXF/TTM, and Background Images if Trimble is selected
      return relevantFormats?.includes("Trimble");
    }
    if (fileType === "svd_svl") {
      // SVD/SVL only shows if Trimble is selected AND client has SVD/SVL in trimble_formats
      return (
        relevantFormats?.includes("Trimble") &&
        trimbleFormats?.some(
          (format) =>
            format?.toLowerCase().includes("svd") &&
            format?.toLowerCase().includes("svl"),
        )
      );
    }
    return false;
  };

  // Memoize the new discrepancy object to prevent infinite re-renders
  const newDiscrepancyForModal = useMemo(
    () => ({
      projectId: project?.id,
    }),
    [project?.id],
  );

  return (
    <div className="export-form">
      {/* Toast Notification */}
      {toast.visible && (
        <div
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 100000,
            backgroundColor:
              toast.type === "success" ? "var(--success)" : "var(--danger)",
            color: "white",
            padding: "var(--space-4) var(--space-6)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-medium)",
            maxWidth: "400px",
            animation: "slideInRight 0.3s ease-out",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "white",
              flexShrink: 0,
            }}
          />
          {toast.message}
        </div>
      )}

      {/* Subitems Status Bar */}
      <div className="export-subitems-status-bar">
        <div className="subitems-status-content">
          <div className="subitems-status-info">
            <span className="subitems-status-label">Subitems Completion:</span>
            <div className="subitems-status-progress">
              <div
                className="subitems-status-progress-bar"
                style={{ width: `${getSubitemsCompletionPercentage()}%` }}
              />
            </div>
            <span className="subitems-status-text">
              {getIncompleteSubitemsCount() === 0 ? (
                <span className="subitems-complete">
                  ✓ All subitems complete
                </span>
              ) : (
                <span className="subitems-incomplete">
                  {getIncompleteSubitemsCount()} incomplete
                </span>
              )}
            </span>
          </div>
          <button
            className="subitems-view-button"
            onClick={() => setSubitemsModalOpen(true)}
            title="View subitems"
          >
            <Eye size={18} />
          </button>
        </div>
      </div>

      <div className="export-form-content">
        {/* Project Information - 2 rows of 3 items */}
        <div className="export-form-section">
          <h3
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-50)",
              marginBottom: "var(--space-4)",
            }}
          >
            Project Information
          </h3>

          {/* First Row: Project, Client, Contact */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "var(--space-4)",
              marginBottom: "var(--space-4)",
            }}
          >
            {/* Project Name */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <FileText
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    Project
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                    }}
                  >
                    {project.name || project.projectName || "Unnamed Project"}
                  </div>
                </div>
              </div>
            </div>

            {/* Client */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <Building
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    Client
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                    }}
                  >
                    {project.clients?.name || project.clientName || "No Client"}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Contact with info button */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <User
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    Primary Contact
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2)",
                    }}
                  >
                    {projectContact && (
                      <button
                        onClick={() => setShowContactModal(true)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--gray-400)",
                          transition: "color var(--transition-fast)",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "var(--cta-blue)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "var(--gray-400)")
                        }
                        title="View contact details"
                      >
                        <Info size={16} />
                      </button>
                    )}
                    <span>{projectContact?.name || "No contact assigned"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Second Row: Additional Model Request Info, Additional Requests, File Formats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "var(--space-4)",
            }}
          >
            {/* Additional Model Request Info */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <Info
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    Additional Model Request Info
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                    }}
                  >
                    {project.model_request_additional_info || "None"}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Requests */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <FileText
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    Additional Requests
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                    }}
                  >
                    {project.additional_requests || "None"}
                  </div>
                </div>
              </div>
            </div>

            {/* File Formats */}
            <div
              style={{
                background: "var(--dark-elevated)",
                border: "1px solid var(--gray-600)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-4)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--space-3)",
                }}
              >
                <FileText
                  size={16}
                  style={{
                    flexShrink: 0,
                    marginTop: "2px",
                    color: "var(--gray-400)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--gray-400)",
                      marginBottom: "var(--space-1)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontWeight: "var(--font-medium)",
                    }}
                  >
                    File Formats
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--gray-50)",
                      wordBreak: "break-word",
                    }}
                  >
                    {project.file_formats_needed?.join(", ") ||
                      project.fileFormatsNeeded?.join(", ") ||
                      "None"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Formats & Upload Date - Equal width layout */}
        <div className="export-form-section">
          <h3
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-50)",
              marginBottom: "var(--space-4)",
            }}
          >
            File Formats & Upload Date
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--space-4)",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--gray-300)",
                  marginBottom: "var(--space-2)",
                }}
              >
                File Formats Needed
              </label>
              <MultiSelectDropdown
                options={availableFileFormats}
                selected={formData.file_formats_needed || []}
                onChange={handleFileFormatsChange}
                placeholder="Select file formats needed for this export..."
              />
            </div>
            <div>
              <Input
                label="Upload Date"
                type="date"
                value={formData.export_date || ""}
                onChange={(e) => {
                  console.log("📅 Date picker changed to:", e.target.value);
                  const updated = {
                    ...formData,
                    export_date: e.target.value,
                  };
                  console.log(
                    "📅 Updated formData.export_date:",
                    updated.export_date,
                  );
                  setFormData(updated);
                  debouncedAutoSave(updated);
                }}
                helperText="Date to use for uploaded files (defaults to today)"
              />
            </div>
          </div>
        </div>

        {/* Design Types - Two-column layout */}
        <div className="export-form-section">
          <h3
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-50)",
              marginBottom: "var(--space-2)",
            }}
          >
            Design Types
          </h3>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--gray-400)",
              marginBottom: "var(--space-4)",
            }}
          >
            Select the design types you want to export. File upload sections
            will appear based on your selections.
          </p>

          {/* Single Column Layout */}
          <div
            style={{
              maxWidth: "500px",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {console.log(
                "📌 Rendering Finish Grade checkbox, checked value:",
                formData.design_finish_grade,
              )}
              <Checkbox
                label="Finish Grade"
                checked={formData.design_finish_grade}
                onChange={(e) =>
                  handleDesignTypeChange(
                    "design_finish_grade",
                    e.target.checked,
                  )
                }
              />
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                label="Rough Grade"
                checked={formData.design_rough_grade}
                onChange={(e) =>
                  handleDesignTypeChange("design_rough_grade", e.target.checked)
                }
              />
            </div>

            {/* Advanced Utility Mode - Expandable */}
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  label="Advanced Utility Mode"
                  checked={advancedUtilityExpanded}
                  onChange={(e) => setAdvancedUtilityExpanded(e.target.checked)}
                />
              </div>

              {advancedUtilityExpanded && (
                <div
                  style={{
                    marginLeft: "var(--space-6)",
                    marginTop: "var(--space-2)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-2)",
                  }}
                >
                  <Checkbox
                    label="Bottom of Storm"
                    checked={formData.design_bottom_of_storm}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_bottom_of_storm",
                        e.target.checked,
                      )
                    }
                  />
                  <Checkbox
                    label="Bottom of Sanitary"
                    checked={formData.design_bottom_of_sanitary}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_bottom_of_sanitary",
                        e.target.checked,
                      )
                    }
                  />
                  <Checkbox
                    label="Bottom of Water"
                    checked={formData.design_bottom_of_water}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_bottom_of_water",
                        e.target.checked,
                      )
                    }
                  />
                </div>
              )}
            </div>

            {/* Erosion Surface - Expandable */}
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  label="Erosion Surface"
                  checked={erosionSurfaceExpanded}
                  onChange={(e) => setErosionSurfaceExpanded(e.target.checked)}
                />
              </div>

              {erosionSurfaceExpanded && (
                <div
                  style={{
                    marginLeft: "var(--space-6)",
                    marginTop: "var(--space-2)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-2)",
                  }}
                >
                  <Checkbox
                    label="Erosion Phase 1"
                    checked={formData.design_erosion_phase_1}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_erosion_phase_1",
                        e.target.checked,
                      )
                    }
                  />
                  <Checkbox
                    label="Erosion Phase 2"
                    checked={formData.design_erosion_phase_2}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_erosion_phase_2",
                        e.target.checked,
                      )
                    }
                  />
                  <Checkbox
                    label="Erosion Phase 3"
                    checked={formData.design_erosion_phase_3}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_erosion_phase_3",
                        e.target.checked,
                      )
                    }
                  />
                  <Checkbox
                    label="Erosion Phase 4"
                    checked={formData.design_erosion_phase_4}
                    onChange={(e) =>
                      handleDesignTypeChange(
                        "design_erosion_phase_4",
                        e.target.checked,
                      )
                    }
                  />
                </div>
              )}
            </div>

            {/* Other with text input */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-3)",
              }}
            >
              <Checkbox
                label="Other"
                checked={formData.design_other}
                onChange={(e) =>
                  handleDesignTypeChange("design_other", e.target.checked)
                }
              />
              {formData.design_other && (
                <input
                  type="text"
                  placeholder="Specify..."
                  value={formData.design_other_name}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      design_other_name: e.target.value,
                    };
                    setFormData(updated);
                    debouncedAutoSave(updated);

                    // Trigger debounced email generation
                    debouncedEmailGeneration();
                  }}
                  style={{
                    padding: "var(--space-1) var(--space-2)",
                    background: "var(--dark-elevated)",
                    border: "1px solid var(--gray-600)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-200)",
                    width: "150px",
                    transition: "all var(--transition-fast)",
                  }}
                  onFocus={(e) => {
                    e.target.style.background = "var(--dark-bg)";
                    e.target.style.borderColor = "var(--cta-blue)";
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.background = "var(--dark-elevated)";
                    e.target.style.borderColor = "var(--gray-600)";
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="export-form-section">
          <h3
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--gray-50)",
              marginBottom: "var(--space-4)",
            }}
          >
            File Uploads
          </h3>
          <div className="export-file-uploads">
            {getSelectedDesignTypes().length === 0 ? (
              <div className="export-file-section">
                <p
                  style={{
                    color: "var(--text-secondary, #6b7280)",
                    fontStyle: "italic",
                  }}
                >
                  No design types selected. Please select design types above to
                  enable file uploads.
                </p>
              </div>
            ) : !formData.file_formats_needed ||
              formData.file_formats_needed.length === 0 ? (
              // Empty state when design types selected but no file formats
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "var(--space-12)",
                  background: "var(--dark-elevated)",
                  border: "1px solid var(--gray-600)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <FileQuestion
                  size={48}
                  style={{
                    color: "var(--gray-400)",
                    marginBottom: "var(--space-4)",
                  }}
                />
                <p
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-medium)",
                    color: "var(--gray-300)",
                    margin: 0,
                    marginBottom: "var(--space-2)",
                  }}
                >
                  No File Formats Selected
                </p>
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-400)",
                    margin: 0,
                    textAlign: "center",
                    maxWidth: "400px",
                  }}
                >
                  Please select file formats needed above to enable file uploads
                  for the selected design types.
                </p>
              </div>
            ) : (
              <>
                {getSelectedDesignTypes().map((designType) => (
                  <div key={designType.key} className="design-files-section">
                    <h4>{designType.name} Files</h4>
                    <div className="design-file-uploads">
                      {/* Only show formats that are selected */}
                      {(formData.file_formats_needed || []).includes(
                        "Topcon",
                      ) && (
                        <div
                          id={`file-upload-${designType.key}_topcon`}
                          className="export-file-section compact file-brand-topcon"
                        >
                          <div className="file-section-header">
                            <h5>
                              <span className="brand-indicator">🔵</span> Topcon
                              Files (.tp3)
                            </h5>
                            <label className="toggle-switch-wrapper">
                              <div
                                className={`toggle-switch ${
                                  !excludedFileTypes[`${designType.key}_topcon`]
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() =>
                                  toggleExcludeFileType(
                                    designType.key,
                                    "topcon",
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleExcludeFileType(
                                      designType.key,
                                      "topcon",
                                    );
                                  }
                                }}
                                tabIndex={0}
                                role="switch"
                                aria-checked={
                                  !excludedFileTypes[`${designType.key}_topcon`]
                                }
                                aria-label="Toggle include Topcon files"
                              >
                                <div className="toggle-slider">
                                  <div className="toggle-knob"></div>
                                </div>
                              </div>
                            </label>
                          </div>
                          {!excludedFileTypes[`${designType.key}_topcon`] && (
                            <FileUpload
                              files={
                                formData[`${designType.key}_topcon_files`] || []
                              }
                              onFilesChange={(files) =>
                                handleFileChange(
                                  designType.key,
                                  "topcon_files",
                                  files,
                                )
                              }
                              projectId={project.id}
                              clientId={project.clientId}
                              maxFiles={20}
                              uploadDate={formData.export_date}
                            />
                          )}
                        </div>
                      )}

                      {(formData.file_formats_needed || []).includes(
                        "Leica",
                      ) && (
                        <div
                          id={`file-upload-${designType.key}_leica`}
                          className="export-file-section compact file-brand-leica"
                        >
                          <div className="file-section-header">
                            <h5>
                              <span className="brand-indicator">🟣</span> Leica
                              Files (.landxml, .dxf, .csv)
                            </h5>
                            <label className="toggle-switch-wrapper">
                              <div
                                className={`toggle-switch ${
                                  !excludedFileTypes[`${designType.key}_leica`]
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() =>
                                  toggleExcludeFileType(designType.key, "leica")
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleExcludeFileType(
                                      designType.key,
                                      "leica",
                                    );
                                  }
                                }}
                                tabIndex={0}
                                role="switch"
                                aria-checked={
                                  !excludedFileTypes[`${designType.key}_leica`]
                                }
                                aria-label="Toggle include Leica files"
                              >
                                <div className="toggle-slider">
                                  <div className="toggle-knob"></div>
                                </div>
                              </div>
                            </label>
                          </div>
                          {!excludedFileTypes[`${designType.key}_leica`] && (
                            <FileUpload
                              files={
                                formData[`${designType.key}_leica_files`] || []
                              }
                              onFilesChange={(files) =>
                                handleFileChange(
                                  designType.key,
                                  "leica_files",
                                  files,
                                )
                              }
                              projectId={project.id}
                              clientId={project.clientId}
                              maxFiles={20}
                              uploadDate={formData.export_date}
                            />
                          )}
                        </div>
                      )}

                      {(formData.file_formats_needed || []).includes(
                        "Trimble",
                      ) && (
                        <>
                          {/* Show VCL files if client supports it or show by default */}
                          {(!trimbleFormats ||
                            trimbleFormats.length === 0 ||
                            trimbleFormats?.some((format) =>
                              format?.toLowerCase().includes("vcl"),
                            )) && (
                            <div
                              id={`file-upload-${designType.key}_vcl`}
                              className="export-file-section compact file-brand-trimble"
                            >
                              <div className="file-section-header">
                                <h5>
                                  <span className="brand-indicator">🟢</span>{" "}
                                  Trimble - VCL Files (.vcl)
                                </h5>
                                <label className="toggle-switch-wrapper">
                                  <div
                                    className={`toggle-switch ${
                                      !excludedFileTypes[
                                        `${designType.key}_vcl`
                                      ]
                                        ? "active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      toggleExcludeFileType(
                                        designType.key,
                                        "vcl",
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleExcludeFileType(
                                          designType.key,
                                          "vcl",
                                        );
                                      }
                                    }}
                                    tabIndex={0}
                                    role="switch"
                                    aria-checked={
                                      !excludedFileTypes[
                                        `${designType.key}_vcl`
                                      ]
                                    }
                                    aria-label="Toggle include Trimble VCL files"
                                  >
                                    <div className="toggle-slider">
                                      <div className="toggle-knob"></div>
                                    </div>
                                  </div>
                                </label>
                              </div>
                              {!excludedFileTypes[`${designType.key}_vcl`] && (
                                <FileUpload
                                  files={
                                    formData[`${designType.key}_vcl_files`] ||
                                    []
                                  }
                                  onFilesChange={(files) =>
                                    handleFileChange(
                                      designType.key,
                                      "vcl_files",
                                      files,
                                    )
                                  }
                                  projectId={project.id}
                                  clientId={project.clientId}
                                  maxFiles={20}
                                  uploadDate={formData.export_date}
                                />
                              )}
                            </div>
                          )}

                          {/* Always show DXF/TTM for Trimble */}
                          <div
                            id={`file-upload-${designType.key}_dxf_ttm`}
                            className="export-file-section compact file-brand-trimble"
                          >
                            <div className="file-section-header">
                              <h5>
                                <span className="brand-indicator">🟢</span>{" "}
                                Trimble - DXF/TTM Files (.dxf, .ttm)
                              </h5>
                              <label className="toggle-switch-wrapper">
                                <div
                                  className={`toggle-switch ${
                                    !excludedFileTypes[
                                      `${designType.key}_dxf_ttm`
                                    ]
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleExcludeFileType(
                                      designType.key,
                                      "dxf_ttm",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleExcludeFileType(
                                        designType.key,
                                        "dxf_ttm",
                                      );
                                    }
                                  }}
                                  tabIndex={0}
                                  role="switch"
                                  aria-checked={
                                    !excludedFileTypes[
                                      `${designType.key}_dxf_ttm`
                                    ]
                                  }
                                  aria-label="Toggle include Trimble DXF/TTM files"
                                >
                                  <div className="toggle-slider">
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            {!excludedFileTypes[
                              `${designType.key}_dxf_ttm`
                            ] && (
                              <FileUpload
                                files={
                                  formData[`${designType.key}_dxf_ttm_files`] ||
                                  []
                                }
                                onFilesChange={(files) =>
                                  handleFileChange(
                                    designType.key,
                                    "dxf_ttm_files",
                                    files,
                                  )
                                }
                                projectId={project.id}
                                clientId={project.clientId}
                                maxFiles={20}
                                uploadDate={formData.export_date}
                              />
                            )}
                          </div>

                          {/* Background Images - Shared Trimble Files */}
                          <div
                            id="file-upload-background-images"
                            className="export-file-section compact file-brand-trimble"
                          >
                            <div className="file-section-header">
                              <h5>
                                <span className="brand-indicator">🟢</span>{" "}
                                Trimble - Background Images (.png, .jpg, .tiff)
                              </h5>
                              <label className="toggle-switch-wrapper">
                                <div
                                  className={`toggle-switch ${
                                    !excludedFileTypes["background_image"]
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleExcludeFileType("background", "image")
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleExcludeFileType(
                                        "background",
                                        "image",
                                      );
                                    }
                                  }}
                                  tabIndex={0}
                                  role="switch"
                                  aria-checked={
                                    !excludedFileTypes["background_image"]
                                  }
                                  aria-label="Toggle include background images"
                                >
                                  <div className="toggle-slider">
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            {!excludedFileTypes["background_image"] && (
                              <FileUpload
                                files={formData.background_image_files || []}
                                onFilesChange={(files) => {
                                  const updated = {
                                    ...formData,
                                    background_image_files: files,
                                  };
                                  setFormData(updated);

                                  // CRITICAL: Update formDataRef immediately so email generation has latest data
                                  formDataRef.current = updated;

                                  debouncedAutoSave(updated, true);

                                  // Trigger email generation when background images are uploaded
                                  console.log(
                                    `📁 Background image upload detected, triggering email generation in 2 seconds...`,
                                  );
                                  debouncedEmailGeneration();
                                }}
                                projectId={project.id}
                                clientId={project.clientId}
                                maxFiles={500}
                                showPreviews={false}
                                uploadDate={formData.export_date}
                              />
                            )}
                          </div>

                          {/* Control CSV - Shared Trimble File (off by default) */}
                          <div
                            id="file-upload-control-csv"
                            className="export-file-section compact file-brand-trimble"
                          >
                            <div className="file-section-header">
                              <h5>
                                <span className="brand-indicator">🟢</span>{" "}
                                Trimble - Control File (.csv)
                              </h5>
                              <label className="toggle-switch-wrapper">
                                <div
                                  className={`toggle-switch ${
                                    !excludedFileTypes["control_csv"]
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleExcludeFileType("control", "csv")
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleExcludeFileType("control", "csv");
                                    }
                                  }}
                                  tabIndex={0}
                                  role="switch"
                                  aria-checked={
                                    !excludedFileTypes["control_csv"]
                                  }
                                  aria-label="Toggle include control CSV file"
                                >
                                  <div className="toggle-slider">
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            {!excludedFileTypes["control_csv"] && (
                              <FileUpload
                                files={formData.control_csv_files || []}
                                onFilesChange={(files) => {
                                  const updated = {
                                    ...formData,
                                    control_csv_files: files,
                                  };
                                  setFormData(updated);

                                  formDataRef.current = updated;

                                  debouncedAutoSave(updated, true);

                                  console.log(
                                    `📁 Control CSV upload detected, triggering email generation in 2 seconds...`,
                                  );
                                  debouncedEmailGeneration();
                                }}
                                projectId={project.id}
                                clientId={project.clientId}
                                maxFiles={1}
                                uploadDate={formData.export_date}
                              />
                            )}
                          </div>

                          {/* TBC Files - Shared Trimble Files (off by default) */}
                          <div
                            id="file-upload-tbc"
                            className="export-file-section compact file-brand-trimble"
                          >
                            <div className="file-section-header">
                              <h5>
                                <span className="brand-indicator">🟢</span>{" "}
                                Trimble - TBC Files (.vce, .zip)
                              </h5>
                              <label className="toggle-switch-wrapper">
                                <div
                                  className={`toggle-switch ${
                                    !excludedFileTypes["tbc"] ? "active" : ""
                                  }`}
                                  onClick={() => {
                                    const wasExcluded =
                                      excludedFileTypes["tbc"];
                                    const newExcluded = {
                                      ...excludedFileTypes,
                                      tbc: !excludedFileTypes["tbc"],
                                    };
                                    setExcludedFileTypes(newExcluded);
                                    debouncedEmailGeneration();
                                    const updated = {
                                      ...formData,
                                      ...(wasExcluded ? {} : { tbc_files: [] }),
                                      excluded_file_types: newExcluded,
                                    };
                                    setFormData(updated);
                                    debouncedAutoSave(updated, true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      const wasExcluded =
                                        excludedFileTypes["tbc"];
                                      const newExcluded = {
                                        ...excludedFileTypes,
                                        tbc: !excludedFileTypes["tbc"],
                                      };
                                      setExcludedFileTypes(newExcluded);
                                      debouncedEmailGeneration();
                                      const updated = {
                                        ...formData,
                                        ...(wasExcluded
                                          ? {}
                                          : { tbc_files: [] }),
                                        excluded_file_types: newExcluded,
                                      };
                                      setFormData(updated);
                                      debouncedAutoSave(updated, true);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="switch"
                                  aria-checked={!excludedFileTypes["tbc"]}
                                  aria-label="Toggle include TBC files"
                                >
                                  <div className="toggle-slider">
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            {!excludedFileTypes["tbc"] && (
                              <FileUpload
                                files={formData.tbc_files || []}
                                onFilesChange={(files) => {
                                  const updated = {
                                    ...formData,
                                    tbc_files: files,
                                  };
                                  setFormData(updated);

                                  formDataRef.current = updated;

                                  debouncedAutoSave(updated, true);

                                  console.log(
                                    `📁 TBC files upload detected, triggering email generation in 2 seconds...`,
                                  );
                                  debouncedEmailGeneration();
                                }}
                                projectId={project.id}
                                clientId={project.clientId}
                                maxFiles={20}
                                uploadDate={formData.export_date}
                              />
                            )}
                          </div>

                          {/* SVD/SVL Files - Always shown */}
                          <div
                            id={`file-upload-${designType.key}_svd_svl`}
                            className="export-file-section compact file-brand-trimble"
                          >
                            <div className="file-section-header">
                              <h5>
                                <span className="brand-indicator">🟢</span>{" "}
                                Trimble - SVD/SVL Files (.svd, .svl)
                              </h5>
                              <label className="toggle-switch-wrapper">
                                <div
                                  className={`toggle-switch ${
                                    !excludedFileTypes[
                                      `${designType.key}_svd_svl`
                                    ]
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleExcludeFileType(
                                      designType.key,
                                      "svd_svl",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleExcludeFileType(
                                        designType.key,
                                        "svd_svl",
                                      );
                                    }
                                  }}
                                  tabIndex={0}
                                  role="switch"
                                  aria-checked={
                                    !excludedFileTypes[
                                      `${designType.key}_svd_svl`
                                    ]
                                  }
                                  aria-label="Toggle include Trimble SVD/SVL files"
                                >
                                  <div className="toggle-slider">
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            {!excludedFileTypes[
                              `${designType.key}_svd_svl`
                            ] && (
                              <FileUpload
                                files={
                                  formData[`${designType.key}_svd_svl_files`] ||
                                  []
                                }
                                onFilesChange={(files) =>
                                  handleFileChange(
                                    designType.key,
                                    "svd_svl_files",
                                    files,
                                  )
                                }
                                projectId={project.id}
                                clientId={project.clientId}
                                maxFiles={20}
                                uploadDate={formData.export_date}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Discrepancies Section */}
        <div className="export-form-section">
          <div className="export-discrepancies-section">
            <div className="export-discrepancies-header">
              <h3
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--gray-50)",
                  margin: 0,
                }}
              >
                Related Discrepancies (Optional)
              </h3>
              <button
                className="btn-create-discrepancy-inline"
                onClick={() => {
                  setShowCreateDiscrepancyModal(true);
                }}
                disabled={saving}
              >
                <Plus size={16} />
                New Discrepancy
              </button>
            </div>

            {loadingDiscrepancies ? (
              <div className="export-discrepancies-loading">
                Loading discrepancies...
              </div>
            ) : projectDiscrepancies.length === 0 ? (
              <div className="export-discrepancies-empty">
                No discrepancies found for this project
              </div>
            ) : (
              <div className="export-discrepancies-list">
                {(() => {
                  console.log(
                    "📋 Rendering discrepancies list. Count:",
                    projectDiscrepancies.length,
                  );
                  console.log(
                    "📋 Selected IDs:",
                    formData.related_discrepancy_ids,
                  );
                  return null;
                })()}
                {projectDiscrepancies.map((discrepancy) => {
                  const isSelected = formData.related_discrepancy_ids?.includes(
                    discrepancy.id,
                  );
                  return (
                    <div
                      key={discrepancy.id}
                      className={`export-discrepancy-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        const discType =
                          discrepancy.discrepancyType || discrepancy.type || "";

                        // Block certain discrepancy types from being attached to exports
                        const blockedTypes = [
                          "Revision Overlays",
                          "PDF/CAD File Discrepancy",
                        ];

                        // If trying to SELECT a blocked type, show warning and prevent
                        if (!isSelected && blockedTypes.includes(discType)) {
                          alert(
                            `"${discType}" cannot be attached to model exports. Only Grading Clarifications, Utility Discrepancies, and Profile Discrepancies can be included.`,
                          );
                          return; // Prevent selection
                        }

                        const currentIds =
                          formData.related_discrepancy_ids || [];
                        const newIds = isSelected
                          ? currentIds.filter((id) => id !== discrepancy.id)
                          : [...currentIds, discrepancy.id];
                        const updated = {
                          ...formData,
                          related_discrepancy_ids: newIds,
                        };
                        setFormData(updated);
                        debouncedAutoSave(updated, true);

                        // Trigger debounced email generation
                        debouncedEmailGeneration();
                      }}
                    >
                      <div className="discrepancy-checkbox">
                        <input type="checkbox" checked={isSelected} readOnly />
                      </div>
                      <div className="discrepancy-info">
                        <span className="discrepancy-type">
                          {discrepancy.discrepancyType || discrepancy.type}
                        </span>
                        <span className="discrepancy-separator">•</span>
                        <span className="discrepancy-created">
                          {new Date(
                            discrepancy.createdAt || discrepancy.created_at,
                          ).toLocaleDateString()}
                        </span>
                        <span className="discrepancy-label">by</span>
                        <span className="discrepancy-creator">
                          {discrepancy.createdByName ||
                            discrepancy.created_by_name ||
                            "Unknown"}
                        </span>
                        <span className="discrepancy-separator">•</span>
                        <span
                          className={`discrepancy-status status-${(discrepancy.discrepancyStatus || discrepancy.status || "").toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {discrepancy.discrepancyStatus || discrepancy.status}
                        </span>
                        {(discrepancy.discrepancyStatus === "Sent" ||
                          discrepancy.status === "Sent") &&
                          (discrepancy.sentDate || discrepancy.sent_date) && (
                            <>
                              <span className="discrepancy-separator">•</span>
                              <span className="discrepancy-label">Sent:</span>
                              <span className="discrepancy-sent-date">
                                {new Date(
                                  discrepancy.sentDate || discrepancy.sent_date,
                                ).toLocaleDateString()}
                              </span>
                            </>
                          )}
                      </div>
                      <button
                        className="discrepancy-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDiscrepancy(discrepancy);
                          setShowEditDiscrepancyModal(true);
                        }}
                        title="Edit discrepancy"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Email Recipients Section */}
        <div className="export-form-section">
          <h3>Email Recipients</h3>
          <div className="export-recipients-section">
            {/* To/CC Fields on Left, Create Contact Button on Right */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--space-4)",
                marginBottom: "var(--space-4)",
              }}
            >
              {/* Left: To and CC Fields Stacked */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                {/* Email To Field */}
                <div className="export-email-field">
                  <label className="export-email-field-label">
                    To: <span className="required">*</span>
                  </label>
                  <div className="export-email-field-input">
                    <MultiSelectDropdown
                      options={clientContacts.map(
                        (contact) =>
                          contact.name +
                          (contact.email ? ` (${contact.email})` : ""),
                      )}
                      selected={(formData.email_to || [])
                        .map((id) => {
                          const contact = clientContacts.find(
                            (c) => c.id === id,
                          );
                          return contact
                            ? contact.name +
                                (contact.email ? ` (${contact.email})` : "")
                            : "";
                        })
                        .filter(Boolean)}
                      onChange={(selectedNames) => {
                        // Only allow ONE recipient in To field
                        const lastSelectedName =
                          selectedNames[selectedNames.length - 1];

                        if (!lastSelectedName) {
                          // If cleared, set to empty
                          const updated = { ...formData, email_to: [] };
                          setFormData(updated);
                          debouncedAutoSave(updated, true);
                          return;
                        }

                        const contact = clientContacts.find(
                          (c) =>
                            c.name + (c.email ? ` (${c.email})` : "") ===
                            lastSelectedName,
                        );

                        const selectedIds = contact ? [contact.id] : []; // Always single-item array
                        const updated = { ...formData, email_to: selectedIds };
                        setFormData(updated);
                        debouncedAutoSave(updated, true); // Immediate save for recipients

                        // Regenerate email to update greeting with new recipient name
                        debouncedEmailGeneration();
                      }}
                      placeholder="Select email recipients..."
                    />
                  </div>
                </div>

                {/* Email CC Field */}
                <div className="export-email-field">
                  <label className="export-email-field-label">CC:</label>
                  <div className="export-email-field-input">
                    <MultiSelectDropdown
                      options={clientContacts
                        .filter(
                          (contact) => !formData.email_to.includes(contact.id),
                        )
                        .map(
                          (contact) =>
                            contact.name +
                            (contact.email ? ` (${contact.email})` : ""),
                        )}
                      selected={(formData.email_cc || [])
                        .map((id) => {
                          const contact = clientContacts.find(
                            (c) => c.id === id,
                          );
                          return contact
                            ? contact.name +
                                (contact.email ? ` (${contact.email})` : "")
                            : "";
                        })
                        .filter(Boolean)}
                      onChange={(selectedNames) => {
                        const selectedIds = selectedNames
                          .map((name) => {
                            const contact = clientContacts.find(
                              (c) =>
                                c.name + (c.email ? ` (${c.email})` : "") ===
                                name,
                            );
                            return contact ? contact.id : null;
                          })
                          .filter(Boolean);
                        const updated = { ...formData, email_cc: selectedIds };
                        setFormData(updated);
                        debouncedAutoSave(updated, true); // Immediate save for CC
                      }}
                      placeholder="Add CC recipients (optional)..."
                    />
                  </div>
                </div>
              </div>

              {/* Right: Create New Contact Button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("+ New Contact button clicked");
                    setShowNewContactModal(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    padding: "var(--space-2) var(--space-4)",
                    background: "var(--cta-orange)",
                    color: "white",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-medium)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  + New Contact
                </button>
              </div>
            </div>

            {/* Email Subject */}
            <div className="export-email-field">
              <label className="export-email-field-label">Subject:</label>
              <div className="export-email-field-input">
                <input
                  type="text"
                  className="export-email-subject-input"
                  value={formData.email_subject}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      email_subject: e.target.value,
                    };
                    setFormData(updated);
                    debouncedAutoSave(updated); // Debounced save for text
                  }}
                  placeholder="Email subject..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Email Body Section - Split View */}
        <div className="export-form-section">
          <h3>Email Content</h3>
          <div className="export-email-container">
            {/* Left Side - Email Editor with Custom Message */}
            <div className="export-email-editor">
              <div className="export-email-editor-header">
                <h4>Custom Message (Optional)</h4>
                <small>Add additional notes to the auto-generated email</small>
              </div>
              <div
                style={{
                  padding: "var(--space-3)",
                  background: "var(--gray-800)",
                  borderRadius: "var(--radius-md)",
                  marginBottom: "var(--space-3)",
                  fontSize: "var(--text-sm)",
                  color: "var(--gray-300)",
                }}
              >
                <p
                  style={{
                    margin: "0 0 var(--space-2) 0",
                    fontWeight: "var(--font-medium)",
                  }}
                >
                  Auto-generated content includes:
                </p>
                <ul style={{ margin: 0, paddingLeft: "var(--space-5)" }}>
                  <li>Greeting with contact name</li>
                  <li>Model download link with formats</li>
                  <li>Attached discrepancies (if any)</li>
                  <li>Appropriate footer message</li>
                  <li>Your signature</li>
                </ul>
                <p
                  style={{
                    margin: "var(--space-2) 0 0 0",
                    fontSize: "var(--text-xs)",
                    fontStyle: "italic",
                  }}
                >
                  Use the field below to add custom notes that will appear
                  before the signature
                </p>
              </div>
              <textarea
                ref={customMessageRef}
                className="export-email-body"
                value={formData.custom_message || ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const updated = { ...formData, custom_message: newValue };
                  setFormData(updated);
                  debouncedAutoSave(updated); // Debounced save
                  // HTML will be regenerated by useEffect watching custom_message
                }}
                placeholder="Add any custom notes here (optional)..."
                rows={8}
                style={{
                  minHeight: "120px",
                }}
              />
            </div>

            {/* Right Side - HTML Preview */}
            <div className="export-email-preview">
              <div className="export-email-preview-header">
                <h4>Email Preview</h4>
                <small>How the email will appear</small>
              </div>
              <div className="export-email-preview-content">
                <iframe
                  srcDoc={
                    formData.email_html ||
                    formatEmailToHTML("", currentUser?.email_signature_html)
                  }
                  title="Email Preview"
                  className="export-email-preview-iframe"
                  sandbox="allow-same-origin allow-scripts"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Project Actions Section - only show if at least one option is available */}
        {(!(
          (freshProjectData?.ready_to_invoice ?? project?.ready_to_invoice) ===
            "Yes" ||
          (freshProjectData?.ready_to_invoice ?? project?.ready_to_invoice) ===
            "None Needed" ||
          (freshProjectData?.ready_to_invoice ?? project?.ready_to_invoice) ===
            "N/A"
        ) ||
          !(
            (freshProjectData?.modeling_status ?? project?.modeling_status) ===
            "Completed"
          )) && (
          <div className="export-form-section">
            <h3
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-semibold)",
                color: "var(--gray-50)",
                marginBottom: "var(--space-4)",
              }}
            >
              Project Actions on Send
            </h3>

            {/* Ready to Invoice - only show if not already Yes, None Needed, or N/A */}
            {!(
              (freshProjectData?.ready_to_invoice ??
                project?.ready_to_invoice) === "Yes" ||
              (freshProjectData?.ready_to_invoice ??
                project?.ready_to_invoice) === "None Needed" ||
              (freshProjectData?.ready_to_invoice ??
                project?.ready_to_invoice) === "N/A"
            ) && (
              <div style={{ marginBottom: "var(--space-6)" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-2)",
                  }}
                >
                  <label
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-medium)",
                      color: "var(--gray-300)",
                    }}
                  >
                    Ready to Invoice
                    {readyToInvoice === null && !hasExplicitlySelectedNo && (
                      <span
                        style={{
                          color: "var(--orange)",
                          fontSize: "var(--text-xs)",
                          marginLeft: "var(--space-2)",
                        }}
                      >
                        (Selection required)
                      </span>
                    )}
                  </label>
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "2px",
                      backgroundColor: "var(--dark-elevated)",
                      border: "1px solid var(--gray-700)",
                      borderRadius: "var(--radius-md)",
                      width: "fit-content",
                    }}
                  >
                    <button
                      type="button"
                      onClick={async () => {
                        setReadyToInvoice("Yes");
                        setHasExplicitlySelectedNo(false);
                        // Save immediately to project
                        try {
                          await supabase
                            .from("projects")
                            .update({ ready_to_invoice: "Yes" })
                            .eq("id", project.id);
                          console.log("✅ Saved ready_to_invoice: Yes");
                        } catch (error) {
                          console.error(
                            "Error saving ready_to_invoice:",
                            error,
                          );
                        }
                      }}
                      style={{
                        padding: "6px 20px",
                        border: "none",
                        borderRadius: "calc(var(--radius-md) - 2px)",
                        backgroundColor:
                          readyToInvoice === "Yes"
                            ? "var(--cta-blue)"
                            : "transparent",
                        color:
                          readyToInvoice === "Yes"
                            ? "white"
                            : "var(--gray-400)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (readyToInvoice !== "Yes") {
                          e.currentTarget.style.color = "var(--gray-200)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (readyToInvoice !== "Yes") {
                          e.currentTarget.style.color = "var(--gray-400)";
                        }
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setReadyToInvoice(null);
                        setHasExplicitlySelectedNo(true);
                        // Save immediately to project (null = No)
                        try {
                          await supabase
                            .from("projects")
                            .update({ ready_to_invoice: null })
                            .eq("id", project.id);
                          console.log("✅ Saved ready_to_invoice: null (No)");
                        } catch (error) {
                          console.error(
                            "Error saving ready_to_invoice:",
                            error,
                          );
                        }
                      }}
                      style={{
                        padding: "6px 20px",
                        border: "none",
                        borderRadius: "calc(var(--radius-md) - 2px)",
                        backgroundColor:
                          readyToInvoice === null && hasExplicitlySelectedNo
                            ? "var(--cta-blue)"
                            : "transparent",
                        color:
                          readyToInvoice === null && hasExplicitlySelectedNo
                            ? "white"
                            : "var(--gray-400)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !(readyToInvoice === null && hasExplicitlySelectedNo)
                        ) {
                          e.currentTarget.style.color = "var(--gray-200)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (
                          !(readyToInvoice === null && hasExplicitlySelectedNo)
                        ) {
                          e.currentTarget.style.color = "var(--gray-400)";
                        }
                      }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setReadyToInvoice("None Needed");
                        setHasExplicitlySelectedNo(false);
                        // Save immediately to project
                        try {
                          await supabase
                            .from("projects")
                            .update({ ready_to_invoice: "None Needed" })
                            .eq("id", project.id);
                          console.log("✅ Saved ready_to_invoice: None Needed");
                        } catch (error) {
                          console.error(
                            "Error saving ready_to_invoice:",
                            error,
                          );
                        }
                      }}
                      style={{
                        padding: "6px 20px",
                        border: "none",
                        borderRadius: "calc(var(--radius-md) - 2px)",
                        backgroundColor:
                          readyToInvoice === "None Needed"
                            ? "var(--cta-blue)"
                            : "transparent",
                        color:
                          readyToInvoice === "None Needed"
                            ? "white"
                            : "var(--gray-400)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (readyToInvoice !== "None Needed") {
                          e.currentTarget.style.color = "var(--gray-200)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (readyToInvoice !== "None Needed") {
                          e.currentTarget.style.color = "var(--gray-400)";
                        }
                      }}
                    >
                      N/A
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Complete Project - only show if not already Completed */}
            {!(
              (freshProjectData?.modeling_status ??
                project?.modeling_status) === "Completed"
            ) && (
              <div style={{ marginBottom: "var(--space-4)" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-2)",
                  }}
                >
                  <label
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-medium)",
                      color: "var(--gray-300)",
                    }}
                  >
                    Complete Project
                  </label>
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "2px",
                      backgroundColor: "var(--dark-elevated)",
                      border: "1px solid var(--gray-700)",
                      borderRadius: "var(--radius-md)",
                      width: "fit-content",
                    }}
                  >
                    <button
                      type="button"
                      onClick={async () => {
                        setCompleteProject(true);
                        // Save immediately to project (set modeling_status to 'Completed')
                        try {
                          await supabase
                            .from("projects")
                            .update({ modeling_status: "Completed" })
                            .eq("id", project.id);
                          console.log("✅ Saved modeling_status: Completed");
                        } catch (error) {
                          console.error("Error saving modeling_status:", error);
                        }
                      }}
                      style={{
                        padding: "6px 20px",
                        border: "none",
                        borderRadius: "calc(var(--radius-md) - 2px)",
                        backgroundColor:
                          completeProject === true
                            ? "var(--cta-blue)"
                            : "transparent",
                        color:
                          completeProject === true
                            ? "white"
                            : "var(--gray-400)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (completeProject !== true) {
                          e.currentTarget.style.color = "var(--gray-200)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (completeProject !== true) {
                          e.currentTarget.style.color = "var(--gray-400)";
                        }
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setCompleteProject(false)}
                      style={{
                        padding: "6px 20px",
                        border: "none",
                        borderRadius: "calc(var(--radius-md) - 2px)",
                        backgroundColor:
                          completeProject === false
                            ? "var(--cta-blue)"
                            : "transparent",
                        color:
                          completeProject === false
                            ? "white"
                            : "var(--gray-400)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-medium)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (completeProject !== false) {
                          e.currentTarget.style.color = "var(--gray-200)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (completeProject !== false) {
                          e.currentTarget.style.color = "var(--gray-400)";
                        }
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--gray-500)",
                marginTop: "var(--space-2)",
                fontStyle: "italic",
              }}
            >
              These actions will be applied to the project when you send the
              email.
            </p>
          </div>
        )}

        {/* Send Email Section */}
        <div className="export-send-section">
          <div className="export-send-left">
            {!isCreating && (
              <Button
                variant="danger"
                size="md"
                onClick={handleDelete}
                disabled={saving}
                icon={<Trash2 size={16} />}
              >
                Delete
              </Button>
            )}
          </div>
          <div className="export-send-right">
            <Button
              variant="secondary"
              size="md"
              onClick={handleMarkAsSent}
              disabled={
                saving ||
                sendingEmail ||
                markingAsSent ||
                formData.status === "sent"
              }
              loading={markingAsSent}
              icon={<CheckCircle size={16} />}
            >
              {markingAsSent ? "Marking..." : "Mark as Sent"}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleSendEmail}
              disabled={
                saving ||
                sendingEmail ||
                !formData.email_to ||
                formData.email_to.length === 0
              }
              loading={sendingEmail}
              icon={<Send size={16} />}
            >
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      </div>

      {/* Subitems Modal */}
      {subitemsModalOpen && (
        <SubitemsModal
          project={project}
          isOpen={subitemsModalOpen}
          onClose={() => {
            setSubitemsModalOpen(false);
            // Reload subitems when modal closes to get updated data
            loadProjectSubitems();
          }}
        />
      )}

      {/* Incomplete Subitems Warning Modal */}
      <IncompleteSubitemsWarningModal
        isOpen={warningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        onOverride={handleOverrideSend}
        incompleteSubitems={projectSubitems.filter((subitem) => {
          const status = subitem.subitemStatus;
          // Consider incomplete if: no status, null, 'Working on it', or 'Stuck'
          return (
            !status ||
            status === null ||
            status === "Working on it" ||
            status === "Stuck"
          );
        })}
      />

      {/* Edit Discrepancy Modal */}
      {showEditDiscrepancyModal && (
        <DiscrepancyModal
          discrepancy={editingDiscrepancy}
          isOpen={showEditDiscrepancyModal}
          isCreating={false}
          onClose={() => {
            setShowEditDiscrepancyModal(false);
            setEditingDiscrepancy(null);
          }}
          onSave={handleSaveDiscrepancy}
          onDelete={handleDeleteDiscrepancy}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          context="export-send"
          zIndex={10000}
        />
      )}

      {/* Create New Discrepancy Modal */}
      {showCreateDiscrepancyModal && (
        <DiscrepancyModal
          discrepancy={newDiscrepancyForModal}
          isOpen={showCreateDiscrepancyModal}
          isCreating={true}
          onClose={() => {
            setShowCreateDiscrepancyModal(false);
          }}
          onSave={async (discrepancyData, closeModal) => {
            const savedDiscrepancy = await handleCreateDiscrepancy(
              discrepancyData,
              closeModal,
            );
            if (savedDiscrepancy && closeModal) {
              setShowCreateDiscrepancyModal(false);
            }
            return savedDiscrepancy;
          }}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          context="export-send"
          zIndex={10000}
        />
      )}

      {/* Contact Details Modal */}
      {showContactModal && projectContact && (
        <ContactDetailsModal
          contact={projectContact}
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          onSave={async (contactId, updates) => {
            // Contact updates are handled by the ContactDetailsModal itself
            // Just close the modal after save
            setShowContactModal(false);
          }}
          zIndex={2000}
        />
      )}

      {/* New Contact Modal */}
      {showNewContactModal && (
        <NewContactModal
          isOpen={showNewContactModal}
          onClose={() => {
            console.log("Closing new contact modal");
            setShowNewContactModal(false);
          }}
          defaultClientId={project?.client_id || project?.clientId}
          zIndex={15000}
          onContactCreated={async (newContact) => {
            // Reload client contacts after creating a new one
            try {
              const { data: contacts } = await supabase
                .from("contacts")
                .select("*")
                .eq("client_id", project.client_id || project.clientId)
                .order("name");

              if (contacts) {
                // Update parent's clientContacts by triggering a refresh
                // For now, we'll just close the modal and the user can refresh
                setShowNewContactModal(false);

                // Show success message
                alert(
                  `Contact "${newContact.name}" created successfully! Please refresh the page to see the new contact in the dropdown.`,
                );
              }
            } catch (error) {
              console.error("Error reloading contacts:", error);
              setShowNewContactModal(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default ExportForm;
